import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import { usePortalLocationOptional } from '../../contexts/PortalLocationContext';
import PortalFooter from '../../components/Layout/PortalFooter';
import portalAPI from '../../services/portalAPI';
import { Html5QrcodeScanner, Html5QrcodeScanType } from 'html5-qrcode';
import { useBarcodeScanner } from '../../hooks/useBarcodeScanner';
import PortalBridgeClubAnalytics from './PortalBridgeClubAnalytics';
import PortalPromotions from './PortalPromotions';
import { TutorialAutoStart, TutorialHelpButton } from '../../components/Portal/Tutorial';
import '../../styles/portal-brand.css';

interface BridgeClubStats {
  total_members: number;
  new_members_this_month: number;
  total_purchases: number;
  purchases_this_month: number;
  total_revenue: number;
  revenue_this_month: number;
  total_red_bridge_bags: number;
  red_bridge_bags_this_month: number;
  total_rewards_redeemed: number;
  rewards_redeemed_this_month: number;
}

interface StoreCredits {
  pending_total: number;
  pending_count: number;
  applied_this_month: number;
  credits: Array<{
    id: string;
    credit_type: string;
    description: string;
    amount: number;
    member_name: string;
    member_code: string;
    status: string;
    created_at: string;
    applied_at: string | null;
  }>;
}

interface BridgeClubMember {
  id: number;
  member_code: string;
  barcode_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  joined_at: string;
  loyalty_progress: number;
  bags_until_reward: number;
  loyalty_reward_available: boolean;
  animals_count: number;
  total_purchases: number;
  last_purchase_date: string | null;
}

interface MemberLookupResult extends BridgeClubMember {
  linked_store_id: number | null;
  linked_store_name: string | null;
  loyalty_bags_purchased: number;
  loyalty_bags_redeemed: number;
  available_rewards: number;
  animals: Array<{
    id: number;
    category: string;
    animal_type: string;
    name: string;
    count: number;
    is_red_bridge: boolean;
  }>;
  created_at: string;
  last_login: string | null;
  // Partner promotions this member can redeem here right now (Phase 2).
  eligible_promotions?: EligiblePromotion[];
}

interface EligiblePromotion {
  id: number;
  name: string;
  title: string;
  message: string;
  percent: number;
  qty: number;
  applies_to: string;
}

interface ReceiptItem {
  name: string;
  quantity: number;
  price: number;
  is_red_bridge?: boolean;
  loyalty_bags?: number;
}

interface AutoRedemption {
  product_name: string;
  msrp_discount: number;
  wholesale_credit: number;
  form_type?: string;
}

interface ScanResult {
  store_name?: string;
  receipt_date?: string;
  items: ReceiptItem[];
  total: number;
  red_bridge_total: number;
  total_loyalty_bags: number;
  // Auto-processed fields
  auto_processed?: boolean;
  bags_purchased?: number;
  bags_redeemed?: number;
  redemptions?: AutoRedemption[];
  total_store_credit?: number;
  discounts_detected?: Array<{ description: string; amount: number }>;
  member_status?: {
    loyalty_progress: number;
    bags_until_reward: number;
    available_rewards: number;
    new_reward_earned: boolean;
  };
}

interface FeedProduct {
  id: number;
  name: string;
  code?: string;
  sku?: string;
  category?: string;
  animal_type?: string;
  feed_grade?: string;
  form?: string;  // 'mash' or 'pellet'
  size_lbs: number;
  bag_size: string;
  wholesale_price: number;
  retail_price: number;
  feed_formula_id: number;
  display_name: string;
  type?: string;
}

interface RedemptionRecord {
  id: number;
  redemption_code: string;
  member_name: string;
  member_code: string;
  product_name: string;
  bag_size: string;
  wholesale_cost: number;
  redeemed_at: string | null;
}

interface FeedBin {
  id: number;
  name: string;
  category: string;  // poultry, dog, cat, horse, etc.
  category_icon: string;
  percent_full: number;
  days_remaining: number | null;
  status: 'critical' | 'low' | 'ok' | 'good' | 'empty' | 'unknown';
  product_name: string;
  current_lbs: number;
  daily_consumption: number;
  default_bag_lbs: number;
}

// Match product names to feed categories
const detectFeedCategory = (productName: string): string[] => {
  const name = productName.toLowerCase();

  // Dog food patterns
  if (name.includes('dog') || name.includes('canine') ||
      name.includes('taste of the wild') || name.includes('totw') ||
      name.includes('diamond naturals') && !name.includes('cat') ||
      name.includes('puppy') || name.includes('k9')) {
    return ['dog'];
  }

  // Cat food patterns
  if (name.includes('cat') || name.includes('feline') || name.includes('kitten')) {
    return ['cat'];
  }

  // Poultry/Chicken patterns
  if (name.includes('layer') || name.includes('chicken') || name.includes('poultry') ||
      name.includes('chick') || name.includes('rooster') || name.includes('hen') ||
      name.includes('broiler') || name.includes('egg') || name.includes('gamebird') ||
      name.includes('turkey') || name.includes('duck') || name.includes('5 star')) {
    return ['poultry'];
  }

  // Horse patterns
  if (name.includes('horse') || name.includes('equine') || name.includes('cavalry') ||
      name.includes('mare') || name.includes('foal')) {
    return ['horse'];
  }

  // Goat patterns
  if (name.includes('goat') || name.includes('caprine') || name.includes('kid')) {
    return ['goat'];
  }

  // Cattle patterns
  if (name.includes('cattle') || name.includes('beef') || name.includes('dairy') ||
      name.includes('calf') || name.includes('bovine') || name.includes('all stock')) {
    return ['cattle'];
  }

  // Sheep patterns
  if (name.includes('sheep') || name.includes('lamb') || name.includes('ovine') ||
      name.includes('ewe')) {
    return ['sheep'];
  }

  // Swine patterns
  if (name.includes('pig') || name.includes('swine') || name.includes('hog') ||
      name.includes('sow') || name.includes('piglet')) {
    return ['swine'];
  }

  // Rabbit patterns
  if (name.includes('rabbit') || name.includes('bunny')) {
    return ['rabbit'];
  }

  // Generic feed - could match multiple
  if (name.includes('feed') || name.includes('grain')) {
    return ['poultry', 'horse', 'goat', 'cattle', 'sheep', 'swine', 'other'];
  }

  return ['other'];
};

type TabType = 'dashboard' | 'checkin' | 'members' | 'promotions' | 'analytics';

// Compress receipt photo on the client before upload.
// iPhone photos are 3–8 MB and can time out the gunicorn worker; HEIC files
// also fail server-side validation. Drawing through a canvas re-encodes to
// JPEG at any quality the browser can decode (Safari handles HEIC natively).
// Returns the bare base64 string (no data: prefix), matching what the API expects.
const compressReceiptImage = (file: File, maxWidth = 1600, quality = 0.75): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Failed to decode image'));
      img.onload = () => {
        const ratio = Math.min(maxWidth / img.width, 1);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * ratio);
        canvas.height = Math.round(img.height * ratio);
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas not supported'));
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(dataUrl.split(',')[1]);
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
};

const PortalBridgeClub: React.FC = () => {
  const { user } = usePortalAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const locationContext = usePortalLocationOptional();
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  const [stats, setStats] = useState<BridgeClubStats | null>(null);
  const [credits, setCredits] = useState<StoreCredits | null>(null);
  const [members, setMembers] = useState<BridgeClubMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Check-in state
  const [checkinQuery, setCheckinQuery] = useState('');
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [checkinResult, setCheckinResult] = useState<MemberLookupResult | null>(null);
  const [checkinError, setCheckinError] = useState('');
  // Partner-promotion redemption (Phase 2): which promo's confirm/optional-value
  // panel is open, the optional 2nd-bag price, in-flight flag, and result banner.
  const [promoApplyingId, setPromoApplyingId] = useState<number | null>(null);
  const [promoDiscount, setPromoDiscount] = useState('');
  const [promoWorking, setPromoWorking] = useState(false);
  const [promoMessage, setPromoMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  // Populated when the backend rejects a receipt photo as unreadable (image_unreadable);
  // rendered as a bullet list under the error message to give the user actionable guidance.
  const [checkinScanSuggestions, setCheckinScanSuggestions] = useState<string[]>([]);
  const [checkinSuccess, setCheckinSuccess] = useState('');
  const [checkinFeedBins, setCheckinFeedBins] = useState<FeedBin[]>([]);
  const [tripPlannerDays, setTripPlannerDays] = useState<number>(14);
  const [showTripPlanner, setShowTripPlanner] = useState(false);

  // QR Scanner state
  const [scannerEnabled, setScannerEnabled] = useState(false);
  const [scannerError, setScannerError] = useState<string | null>(null);
  const scannerRef = useRef<Html5QrcodeScanner | null>(null);
  const scannerInitialized = useRef(false);

  // Purchase recording
  const [recordingPurchase, setRecordingPurchase] = useState(false);
  const [purchaseBags, setPurchaseBags] = useState('');
  const [purchaseTotal, setPurchaseTotal] = useState('');

  // Reworked manual-entry form (POS journal + product line items)
  const [purchaseJournal, setPurchaseJournal] = useState('');
  const [purchaseDate, setPurchaseDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [purchaseTime, setPurchaseTime] = useState<string>(() => new Date().toTimeString().slice(0, 5));
  const [purchaseCashier, setPurchaseCashier] = useState('');
  const [purchaseRegister, setPurchaseRegister] = useState('');
  const [purchaseNotes, setPurchaseNotes] = useState('');
  const [purchaseFreeBag, setPurchaseFreeBag] = useState(false);
  const [purchaseLineItems, setPurchaseLineItems] = useState<{ product_id: string; quantity: string; unit_price: string }[]>(
    [{ product_id: '', quantity: '1', unit_price: '' }]
  );
  const [bcProducts, setBcProducts] = useState<{ id: number; name: string; animal_type: string; bag_size_lbs: number }[]>([]);
  const [dupWarning, setDupWarning] = useState<{ message: string; existing_id: number } | null>(null);
  const [dupBlock, setDupBlock] = useState<{ message: string; existing_id: number } | null>(null);

  // Load the retail-partner product catalog for the manual-entry dropdown
  useEffect(() => {
    portalAPI.get('/portal/bridge-club/products/')
      .then((res) => setBcProducts(res.data?.products || []))
      .catch(() => setBcProducts([]));
  }, []);

  // Inline receipt scanning during check-in
  const [showReceiptInCheckin, setShowReceiptInCheckin] = useState(false);
  const [checkinReceiptImage, setCheckinReceiptImage] = useState<string | null>(null);
  const [checkinReceiptBase64, setCheckinReceiptBase64] = useState<string | null>(null);
  const [checkinScanResult, setCheckinScanResult] = useState<ScanResult | null>(null);
  const [checkinScanning, setCheckinScanning] = useState(false);
  const [checkinSaving, setCheckinSaving] = useState(false);
  const checkinFileInputRef = useRef<HTMLInputElement>(null);

  // Bin assignment prompt after receipt
  const [showBinAssignPrompt, setShowBinAssignPrompt] = useState(false);
  const [binAssignments, setBinAssignments] = useState<Record<number, { selected: boolean; quantity: number }>>({});
  const [savingBinAssignments, setSavingBinAssignments] = useState(false);
  const [lastPurchaseItems, setLastPurchaseItems] = useState<ReceiptItem[]>([]);

  // Camera-based receipt scanning
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Feed product selection for redemption
  const [feedProducts, setFeedProducts] = useState<FeedProduct[]>([]);
  const [groupedProducts, setGroupedProducts] = useState<Record<string, FeedProduct[]>>({});
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<FeedProduct | null>(null);
  const [redeemingReward, setRedeemingReward] = useState(false);

  // Redemption history modal
  const [showRedemptionHistory, setShowRedemptionHistory] = useState(false);
  const [redemptionHistory, setRedemptionHistory] = useState<RedemptionRecord[]>([]);
  const [redemptionStats, setRedemptionStats] = useState({ this_month: 0, total_all_time: 0 });

  // Credits detail modal
  const [showCreditsDetail, setShowCreditsDetail] = useState(false);

  // Receipt scanning state
  const [receiptMember, setReceiptMember] = useState<MemberLookupResult | null>(null);
  const [receiptMemberQuery, setReceiptMemberQuery] = useState('');
  const [receiptMemberLoading, setReceiptMemberLoading] = useState(false);
  const [receiptMemberError, setReceiptMemberError] = useState('');
  const [receiptImage, setReceiptImage] = useState<string | null>(null);
  const [receiptImageBase64, setReceiptImageBase64] = useState<string | null>(null);
  const [scanningReceipt, setScanningReceipt] = useState(false);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [savingReceipt, setSavingReceipt] = useState(false);
  const [receiptSuccess, setReceiptSuccess] = useState('');
  const [receiptError, setReceiptError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reload data when location changes (for multi-location retail partners)
  useEffect(() => {
    loadData();
  }, [locationContext?.selectedLocation?.id]);

  // Handle URL parameters for tab navigation and member auto-load
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const memberParam = searchParams.get('member');

    // Set active tab from URL param
    if (tabParam && ['dashboard', 'checkin', 'members', 'promotions', 'analytics'].includes(tabParam)) {
      setActiveTab(tabParam as TabType);
    }

    // If member param is provided, auto-load that member in check-in
    if (memberParam && tabParam === 'checkin') {
      setCheckinQuery(memberParam);
      handleCheckinSearch(memberParam);
      // Clear URL params after loading
      setSearchParams({});
    }
  }, [searchParams]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, membersRes, creditsRes, productsRes] = await Promise.all([
        portalAPI.get('/portal/bridge-club/stats/'),
        portalAPI.get('/portal/bridge-club/members/'),
        portalAPI.get('/portal/bridge-club/credits/'),
        portalAPI.get('/portal/bridge-club/feed_products/'),
      ]);
      setStats(statsRes.data);
      setMembers(membersRes.data);
      setCredits(creditsRes.data);
      setFeedProducts(productsRes.data.products || []);
      setGroupedProducts(productsRes.data.grouped_by_animal || {});
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load Bridge Club data');
    } finally {
      setLoading(false);
    }
  };

  const loadRedemptionHistory = async () => {
    try {
      const response = await portalAPI.get('/portal/bridge-club/redemption_history/');
      setRedemptionHistory(response.data.redemptions || []);
      setRedemptionStats({
        this_month: response.data.this_month || 0,
        total_all_time: response.data.total_all_time || 0,
      });
    } catch (err) {
      console.error('Failed to load redemption history', err);
    }
  };

  const openRedeemModal = () => {
    setSelectedProduct(null);
    setShowRedeemModal(true);
  };

  const closeRedeemModal = () => {
    setShowRedeemModal(false);
    setSelectedProduct(null);
  };

  // Parse member code from QR data (URL or raw code)
  const parseQRCode = useCallback((qrData: string): string | null => {
    // Try to parse as URL first
    try {
      const url = new URL(qrData);
      const code = url.searchParams.get('code');
      if (code) return code.toUpperCase();
    } catch {
      // Not a URL, check if it's a raw member code
    }

    // Check if it matches member code pattern (RBF-XXXXXX)
    const codeMatch = qrData.match(/RBF-[A-Z0-9]{6}/i);
    if (codeMatch) {
      return codeMatch[0].toUpperCase();
    }

    return null;
  }, []);

  // Handle successful QR scan
  const onQRCodeScanned = useCallback((decodedText: string) => {
    console.log('[QR Scanner] Scanned:', decodedText);

    const memberCode = parseQRCode(decodedText);
    if (memberCode) {
      // Stop scanner and search for member
      if (scannerRef.current) {
        scannerRef.current.clear().catch(console.error);
      }
      setScannerEnabled(false);
      scannerInitialized.current = false;
      setCheckinQuery(memberCode);
      handleCheckinSearch(memberCode);
    } else {
      setScannerError('Invalid QR code. Please try again or use manual lookup.');
      setTimeout(() => setScannerError(null), 3000);
    }
  }, [parseQRCode]);

  // Initialize QR scanner
  useEffect(() => {
    if (!scannerEnabled || activeTab !== 'checkin') {
      return;
    }

    // Prevent double initialization
    if (scannerInitialized.current) {
      return;
    }

    const initScanner = () => {
      const element = document.getElementById('qr-reader');
      if (!element) {
        console.log('[QR Scanner] Element not found, retrying...');
        setTimeout(initScanner, 100);
        return;
      }

      scannerInitialized.current = true;

      const scanner = new Html5QrcodeScanner(
        'qr-reader',
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1,
          supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA],
          rememberLastUsedCamera: true,
          showTorchButtonIfSupported: true,
        },
        false
      );

      scanner.render(
        (decodedText) => {
          onQRCodeScanned(decodedText);
        },
        () => {
          // Ignore scanning errors (happens continuously while scanning)
        }
      );

      scannerRef.current = scanner;
    };

    const timeout = setTimeout(initScanner, 200);

    return () => {
      clearTimeout(timeout);
      if (scannerRef.current) {
        scannerRef.current.clear().catch(console.error);
        scannerRef.current = null;
        scannerInitialized.current = false;
      }
    };
  }, [scannerEnabled, activeTab, onQRCodeScanned]);

  // Clean up scanner when changing tabs
  useEffect(() => {
    if (activeTab !== 'checkin' && scannerRef.current) {
      scannerRef.current.clear().catch(console.error);
      scannerRef.current = null;
      scannerInitialized.current = false;
      setScannerEnabled(false);
    }
  }, [activeTab]);

  const handleEnableScanner = () => {
    scannerInitialized.current = false;
    setScannerEnabled(true);
    setScannerError(null);
  };

  const handleDisableScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.clear().catch(console.error);
      scannerRef.current = null;
    }
    scannerInitialized.current = false;
    setScannerEnabled(false);
  };

  // Hardware barcode scanner detection
  // Detects rapid keyboard input from USB/Bluetooth barcode scanners
  const handleBarcodeScan = useCallback(async (barcode: string) => {
    console.log('[Barcode Scanner] Detected:', barcode);

    // Only process on checkin tab
    if (activeTab !== 'checkin') return;

    // Set the query and search
    setCheckinQuery(barcode);
    setCheckinLoading(true);
    setCheckinError('');
    setCheckinResult(null);
    setCheckinSuccess('');

    try {
      const response = await portalAPI.get(`/portal/bridge-club/lookup/?q=${encodeURIComponent(barcode)}`);
      setCheckinResult(response.data);
      setCheckinSuccess('Member found via barcode scan!');
      setTimeout(() => setCheckinSuccess(''), 3000);
    } catch (err: any) {
      setCheckinError(err.response?.data?.detail || 'Member not found');
    } finally {
      setCheckinLoading(false);
    }
  }, [activeTab]);

  // Enable barcode scanner detection on checkin tab
  useBarcodeScanner({
    onScan: handleBarcodeScan,
    enabled: activeTab === 'checkin',
    minLength: 8,
    prefixFilter: '5', // Bridge Club barcodes start with 5
  });

  const handleCheckinSearch = async (query?: string) => {
    const searchValue = query || checkinQuery;
    if (!searchValue.trim()) return;

    setCheckinLoading(true);
    setCheckinError('');
    setCheckinResult(null);
    setCheckinSuccess('');
    setCheckinFeedBins([]);

    try {
      const response = await portalAPI.get(`/portal/bridge-club/lookup/?q=${encodeURIComponent(searchValue)}`);
      setCheckinResult(response.data);

      // Fetch member detail to get feed bins
      try {
        const detailResponse = await portalAPI.get(`/portal/bridge-club/${response.data.id}/member_detail/`);
        setCheckinFeedBins(detailResponse.data.feed_bins || []);
      } catch {
        // Silently fail - feed bins are optional
      }
    } catch (err: any) {
      setCheckinError(err.response?.data?.error || 'Member not found');
    } finally {
      setCheckinLoading(false);
    }
  };

  const resetManualPurchaseForm = () => {
    setPurchaseJournal('');
    setPurchaseCashier('');
    setPurchaseRegister('');
    setPurchaseNotes('');
    setPurchaseFreeBag(false);
    setPurchaseTotal('');
    setPurchaseBags('');
    setPurchaseLineItems([{ product_id: '', quantity: '1', unit_price: '' }]);
    setPurchaseDate(new Date().toISOString().slice(0, 10));
    setPurchaseTime(new Date().toTimeString().slice(0, 5));
  };

  const manualPurchaseTotal = () =>
    purchaseLineItems.reduce(
      (sum, li) => sum + (parseFloat(li.unit_price) || 0) * (parseInt(li.quantity) || 0),
      0
    );

  const submitManualPurchase = async (force = false) => {
    if (!checkinResult) return;

    const items = purchaseLineItems
      .filter((li) => li.product_id)
      .map((li) => {
        const prod = bcProducts.find((p) => String(p.id) === String(li.product_id));
        return {
          product_id: Number(li.product_id),
          name: prod?.name,
          quantity: parseInt(li.quantity) || 1,
          unit_price: parseFloat(li.unit_price) || 0,
          is_red_bridge: true,
          bag_size_lbs: prod?.bag_size_lbs || 40,
        };
      });

    const computedTotal = manualPurchaseTotal();
    const total = computedTotal || parseFloat(purchaseTotal) || 0;
    const bags = items.reduce((s, it) => s + it.quantity, 0) || (parseInt(purchaseBags) || 0);

    if (!purchaseFreeBag && bags === 0 && total === 0) {
      setCheckinError('Add a product or enter a total amount');
      return;
    }

    let transactionTimestamp: string | undefined;
    if (purchaseDate) {
      const dt = new Date(`${purchaseDate}T${purchaseTime || '12:00'}`);
      if (!isNaN(dt.getTime())) transactionTimestamp = dt.toISOString();
    }

    setRecordingPurchase(true);
    setCheckinError('');
    setDupWarning(null);

    try {
      const url = '/portal/bridge-club/record_purchase/' + (force ? '?force=true' : '');
      const response = await portalAPI.post(url, {
        member_id: checkinResult.id,
        journal_number: purchaseJournal || undefined,
        transaction_timestamp: transactionTimestamp,
        register_id: purchaseRegister || undefined,
        cashier: purchaseCashier || undefined,
        included_free_bag: purchaseFreeBag,
        items: items.length ? items : undefined,
        red_bridge_bags: items.length ? undefined : bags,
        total,
        notes: purchaseNotes || undefined,
      });

      // Soft duplicate warning (no journal number) — let the lead confirm
      if (response.data?.warning === 'possible_duplicate') {
        setDupWarning({ message: response.data.message, existing_id: response.data.existing_id });
        return;
      }

      setCheckinSuccess(
        `Purchase recorded! ${checkinResult.first_name} now has ${response.data.member_loyalty_progress}/10 punches` +
        (response.data.member_reward_available ? ' - REWARD AVAILABLE!' : '')
      );
      resetManualPurchaseForm();

      const updatedMember = await portalAPI.get(`/portal/bridge-club/lookup/?q=${checkinResult.member_code}`);
      setCheckinResult(updatedMember.data);
      loadData();
    } catch (err: any) {
      if (err.response?.status === 409) {
        setDupBlock({
          message: err.response.data?.message || 'This receipt has already been recorded.',
          existing_id: err.response.data?.existing_id,
        });
      } else {
        setCheckinError(err.response?.data?.message || err.response?.data?.error || 'Failed to record purchase');
      }
    } finally {
      setRecordingPurchase(false);
    }
  };

  const handleRecordPurchase = () => submitManualPurchase(false);

  const addPurchaseLineItem = () =>
    setPurchaseLineItems((prev) => [...prev, { product_id: '', quantity: '1', unit_price: '' }]);

  const updatePurchaseLineItem = (idx: number, field: 'product_id' | 'quantity' | 'unit_price', value: string) =>
    setPurchaseLineItems((prev) => prev.map((li, i) => (i === idx ? { ...li, [field]: value } : li)));

  const removePurchaseLineItem = (idx: number) =>
    setPurchaseLineItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  // Redeem a partner promotion for the looked-up member. Records the one-time
  // redemption here; the cashier applies the actual discount on their own POS.
  // `discountValue` (the 2nd-bag price) is optional — sent only if entered.
  const handleApplyPromotion = async (promo: EligiblePromotion, discountValue?: string) => {
    if (!checkinResult) return;
    setPromoWorking(true);
    setPromoMessage(null);
    try {
      const res = await portalAPI.post('/portal/bridge-club/redeem-promotion/', {
        member_id: checkinResult.id,
        promotion_id: promo.id,
        ...(discountValue ? { discount_value: discountValue } : {}),
      });
      setPromoMessage({ type: 'success', text: res.data.message || 'Promotion recorded.' });
      // Drop the now-redeemed promo so the prompt clears.
      setCheckinResult({
        ...checkinResult,
        eligible_promotions: (checkinResult.eligible_promotions || []).filter(p => p.id !== promo.id),
      });
      setPromoApplyingId(null);
      setPromoDiscount('');
    } catch (err: any) {
      setPromoMessage({ type: 'error', text: err.response?.data?.error_message || 'Could not apply the promotion.' });
    } finally {
      setPromoWorking(false);
    }
  };

  const handleRedeemReward = async () => {
    if (!checkinResult || !selectedProduct) return;

    setRedeemingReward(true);
    setCheckinError('');

    try {
      const formLabel = selectedProduct.form === 'pellet' ? 'Pellet' : 'Mash';
      const response = await portalAPI.post('/portal/bridge-club/redeem_reward/', {
        member_id: checkinResult.id,
        feed_formula_id: selectedProduct.feed_formula_id || selectedProduct.id,
        product_name: selectedProduct.name,
        form_type: selectedProduct.form || 'mash',
        bag_size: selectedProduct.bag_size,
        wholesale_cost: selectedProduct.wholesale_price,
      });

      setCheckinSuccess(
        `Reward redeemed! ${response.data.product_name} ${formLabel} (${response.data.bag_size})\n` +
        `Code: ${response.data.redemption_code}\n` +
        `Credit added to your account.`
      );

      // Close modal and reset
      closeRedeemModal();

      // Refresh the member info
      const updatedMember = await portalAPI.get(`/portal/bridge-club/lookup/?q=${checkinResult.member_code}`);
      setCheckinResult(updatedMember.data);

      // Refresh stats
      loadData();
    } catch (err: any) {
      setCheckinError(err.response?.data?.error || 'Failed to redeem reward');
    } finally {
      setRedeemingReward(false);
    }
  };

  const clearCheckin = () => {
    setCheckinQuery('');
    setCheckinResult(null);
    setCheckinError('');
    setCheckinSuccess('');
    setPurchaseBags('');
    setPurchaseTotal('');
    setCheckinFeedBins([]);
    // Clear inline receipt state
    setShowReceiptInCheckin(false);
    setCheckinReceiptImage(null);
    setCheckinReceiptBase64(null);
    setCheckinScanResult(null);
  };

  // Handle file select for inline receipt scanning in check-in
  const handleCheckinReceiptSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setCheckinError('Please select an image file');
      return;
    }

    try {
      const base64 = await compressReceiptImage(file);
      setCheckinReceiptImage(`data:image/jpeg;base64,${base64}`);
      setCheckinReceiptBase64(base64);
      setCheckinScanSuggestions([]);
      await scanCheckinReceipt(base64);
    } catch (err) {
      console.error('Receipt compression failed:', err);
      setCheckinError('Could not process that photo. Try a different image.');
      setCheckinScanSuggestions([]);
    }
  };

  // Scan receipt during check-in flow
  const scanCheckinReceipt = async (base64: string) => {
    if (!checkinResult) return;

    setCheckinScanning(true);
    setCheckinError('');
    setCheckinScanSuggestions([]);
    setCheckinScanResult(null);

    try {
      const response = await portalAPI.post('/portal/bridge-club/scan_receipt/', {
        receipt_image: base64,
        member_id: checkinResult.id,
        auto_process: true,  // Enable smart scanning with free bag detection
      });

      setCheckinScanResult(response.data);
    } catch (err: any) {
      const data = err.response?.data;
      setCheckinError(data?.error || 'Failed to scan receipt. Please try again or enter manually.');
      // image_unreadable returns a suggestions list — show it instead of the generic placeholder UX
      if (data?.error_code === 'image_unreadable' && Array.isArray(data?.suggestions)) {
        setCheckinScanSuggestions(data.suggestions);
      }
      // Set a placeholder result for manual entry
      setCheckinScanResult({
        items: [{ name: '', quantity: 1, price: 0, is_red_bridge: false, loyalty_bags: 0 }],
        total: 0,
        red_bridge_total: 0,
        total_loyalty_bags: 0,
      });
    } finally {
      setCheckinScanning(false);
    }
  };

  // Camera-based receipt scanning functions
  const startCamera = async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Use rear camera
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      });
      streamRef.current = stream;
      // Set cameraActive FIRST to render the video element
      setCameraActive(true);
    } catch (err: any) {
      console.error('Camera error:', err);
      if (err.name === 'NotAllowedError') {
        setCameraError('Camera access denied. Please allow camera permissions and try again.');
      } else if (err.name === 'NotFoundError') {
        setCameraError('No camera found on this device.');
      } else if (err.name === 'NotReadableError') {
        setCameraError('Camera is in use by another application.');
      } else {
        setCameraError('Unable to access camera. Please use "Upload from device" instead.');
      }
      setCameraActive(false);
    }
  };

  // Connect stream to video element once cameraActive is true and video element is rendered
  useEffect(() => {
    if (cameraActive && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.onloadedmetadata = () => {
        videoRef.current?.play().catch(err => {
          console.error('Error playing video:', err);
          setCameraError('Unable to start camera preview.');
        });
      };
    }
  }, [cameraActive]);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Ensure we have video dimensions
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      setCameraError('Camera not ready. Please wait a moment and try again.');
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    const base64 = dataUrl.split(',')[1];

    // Stop camera and set image
    stopCamera();
    setCheckinReceiptImage(dataUrl);
    setCheckinReceiptBase64(base64);

    // Start scanning
    scanCheckinReceipt(base64);
  };

  // Handle tap-to-focus on camera
  const handleTapToFocus = async (event: React.MouseEvent<HTMLVideoElement>) => {
    if (!streamRef.current || !videoRef.current) return;

    const video = videoRef.current;
    const rect = video.getBoundingClientRect();

    // Calculate tap position as percentage
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;

    // Get the video track
    const videoTrack = streamRef.current.getVideoTracks()[0];
    if (!videoTrack) return;

    // Check if focusMode is supported
    const capabilities = videoTrack.getCapabilities?.();
    if (!capabilities) return;

    try {
      // Try using ImageCapture API for focus (better support)
      if ('ImageCapture' in window) {
        const imageCapture = new (window as any).ImageCapture(videoTrack);
        const photoCapabilities = await imageCapture.getPhotoCapabilities?.().catch(() => null);

        if (photoCapabilities) {
          // Some devices support focus through ImageCapture
          await imageCapture.setOptions?.({
            focusMode: 'manual',
            focusDistance: photoCapabilities.focusDistance?.min || 0,
          }).catch(() => {});
        }
      }

      // Try using track constraints for focus
      if ((capabilities as any).focusMode?.includes('manual') ||
          (capabilities as any).focusMode?.includes('single-shot')) {
        await videoTrack.applyConstraints({
          advanced: [{
            // @ts-ignore - focusMode and pointsOfInterest may not be in types
            focusMode: 'single-shot',
            pointsOfInterest: [{ x, y }],
          }]
        } as any).catch(() => {});
      } else if ((capabilities as any).focusMode?.includes('continuous')) {
        // Trigger continuous autofocus if that's all that's available
        await videoTrack.applyConstraints({
          advanced: [{
            // @ts-ignore
            focusMode: 'continuous',
          }]
        } as any).catch(() => {});
      }

      // Visual feedback - show focus indicator briefly
      const indicator = document.createElement('div');
      indicator.style.cssText = `
        position: absolute;
        left: ${event.clientX - rect.left - 25}px;
        top: ${event.clientY - rect.top - 25}px;
        width: 50px;
        height: 50px;
        border: 2px solid #fff;
        border-radius: 50%;
        pointer-events: none;
        animation: focusPulse 0.5s ease-out forwards;
      `;
      video.parentElement?.appendChild(indicator);
      setTimeout(() => indicator.remove(), 500);
    } catch (err) {
      console.log('Tap to focus not supported on this device');
    }
  };

  // Add CSS animation for tap-to-focus indicator
  useEffect(() => {
    if (!document.querySelector('style[data-focus-pulse]')) {
      const style = document.createElement('style');
      style.setAttribute('data-focus-pulse', 'true');
      style.textContent = `
        @keyframes focusPulse {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(1.5); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Cleanup camera on unmount or when receipt section closes
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  useEffect(() => {
    if (!showReceiptInCheckin) {
      stopCamera();
    }
  }, [showReceiptInCheckin]);

  // Update item in check-in receipt
  const handleCheckinItemChange = (index: number, field: keyof ReceiptItem, value: any) => {
    if (!checkinScanResult) return;

    const newItems = [...checkinScanResult.items];
    newItems[index] = { ...newItems[index], [field]: value };

    // Recalculate totals
    const redBridgeItems = newItems.filter(item => item.is_red_bridge);
    const total = newItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const redBridgeTotal = redBridgeItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const totalLoyaltyBags = redBridgeItems.reduce((sum, item) => sum + (item.loyalty_bags || item.quantity), 0);

    setCheckinScanResult({
      ...checkinScanResult,
      items: newItems,
      total,
      red_bridge_total: redBridgeTotal,
      total_loyalty_bags: totalLoyaltyBags,
    });
  };

  const handleCheckinAddItem = () => {
    if (!checkinScanResult) return;
    setCheckinScanResult({
      ...checkinScanResult,
      items: [...checkinScanResult.items, { name: '', quantity: 1, price: 0, is_red_bridge: false, loyalty_bags: 0 }],
    });
  };

  const handleCheckinRemoveItem = (index: number) => {
    if (!checkinScanResult) return;
    const newItems = checkinScanResult.items.filter((_, i) => i !== index);

    const redBridgeItems = newItems.filter(item => item.is_red_bridge);
    const total = newItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const redBridgeTotal = redBridgeItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const totalLoyaltyBags = redBridgeItems.reduce((sum, item) => sum + (item.loyalty_bags || item.quantity), 0);

    setCheckinScanResult({
      ...checkinScanResult,
      items: newItems,
      total,
      red_bridge_total: redBridgeTotal,
      total_loyalty_bags: totalLoyaltyBags,
    });
  };

  // Submit receipt during check-in flow
  const handleCheckinReceiptSubmit = async () => {
    if (!checkinResult || !checkinScanResult) return;

    setCheckinSaving(true);
    setCheckinError('');

    try {
      // Calculate totals from items, sanitizing NaN values
      const redBridgeItems = checkinScanResult.items.filter(i => i.is_red_bridge);
      const totalLoyaltyBags = redBridgeItems.reduce((sum, i) => sum + (i.quantity || 0), 0);

      // Sanitize items - remove NaN prices, keep only essential fields
      const sanitizedItems = checkinScanResult.items.map(item => ({
        name: item.name || 'Unknown Item',
        quantity: item.quantity || 1,
        price: isNaN(item.price) ? 0 : (item.price || 0),
        is_red_bridge: item.is_red_bridge || false,
      }));

      // Calculate total, defaulting to 0 if NaN
      const total = isNaN(checkinScanResult.total) ? 0 : (checkinScanResult.total || 0);

      console.log('Submitting purchase:', {
        member_id: checkinResult.id,
        red_bridge_bags: totalLoyaltyBags,
        total,
        items: sanitizedItems,
        hasImage: !!checkinReceiptBase64,
      });

      const response = await portalAPI.post('/portal/bridge-club/record_purchase/', {
        member_id: checkinResult.id,
        red_bridge_bags: totalLoyaltyBags,
        total: total,
        items: sanitizedItems,
        receipt_image: checkinReceiptBase64,
      });

      const redBridgeBags = response.data.red_bridge_bags || 0;
      setCheckinSuccess(
        `Purchase recorded for ${checkinResult.first_name}! ` +
        `${redBridgeBags} Red Bridge bag${redBridgeBags !== 1 ? 's' : ''} added to loyalty (${response.data.member_loyalty_progress}/10)` +
        (response.data.member_reward_available ? ' - REWARD AVAILABLE!' : '')
      );

      // Save items for bin assignment before clearing
      const purchasedItems = [...checkinScanResult.items];
      setLastPurchaseItems(purchasedItems);

      // Refresh member info
      const updatedMember = await portalAPI.get(`/portal/bridge-club/lookup/?q=${checkinResult.member_code}`);
      setCheckinResult(updatedMember.data);

      // Reset inline receipt state
      setShowReceiptInCheckin(false);
      setCheckinReceiptImage(null);
      setCheckinReceiptBase64(null);
      setCheckinScanResult(null);

      // Check if member has feed bins - show bin assignment prompt for ALL products (not just Red Bridge)
      if (checkinFeedBins.length > 0 && purchasedItems.length > 0) {
        // Initialize bin assignments with defaults
        const initialAssignments: Record<number, { selected: boolean; quantity: number }> = {};
        checkinFeedBins.forEach(bin => {
          initialAssignments[bin.id] = { selected: false, quantity: 1 };
        });
        setBinAssignments(initialAssignments);
        setShowBinAssignPrompt(true);
      }

      // Refresh stats
      loadData();
    } catch (err: any) {
      console.error('Purchase save error:', err.response?.data || err.message);
      const errorMsg = err.response?.data?.error ||
                       err.response?.data?.detail ||
                       err.response?.data?.message ||
                       (typeof err.response?.data === 'string' ? err.response.data : null) ||
                       err.message ||
                       'Failed to save purchase';
      setCheckinError(`Failed to save: ${errorMsg}`);
    } finally {
      setCheckinSaving(false);
    }
  };

  // Handle bin assignment submission
  const handleBinAssignmentSubmit = async () => {
    if (!checkinResult) return;

    setSavingBinAssignments(true);
    setCheckinError('');

    try {
      const selectedBins = Object.entries(binAssignments)
        .filter(([_, assignment]) => assignment.selected)
        .map(([binId, assignment]) => ({
          bin_id: parseInt(binId),
          quantity: assignment.quantity,
        }));

      if (selectedBins.length === 0) {
        setShowBinAssignPrompt(false);
        return;
      }

      // Record feed transactions for each selected bin
      for (const { bin_id, quantity } of selectedBins) {
        const bin = checkinFeedBins.find(b => b.id === bin_id);
        if (bin) {
          const lbs = quantity * (bin.default_bag_lbs || 40);
          await portalAPI.post('/portal/bridge-club/feed-bin-transaction/', {
            member_id: checkinResult.id,
            bin_id: bin_id,
            lbs_added: lbs,
            notes: `Added from receipt: ${quantity} bag${quantity !== 1 ? 's' : ''}`,
          });
        }
      }

      setCheckinSuccess(prev => prev + ' Feed bins updated!');
      setShowBinAssignPrompt(false);

      // Refresh feed bins
      try {
        const binsResponse = await portalAPI.get(`/portal/bridge-club/member-feed-bins/${checkinResult.id}/`);
        setCheckinFeedBins(binsResponse.data.bins || []);
      } catch (e) {
        // Ignore errors refreshing bins
      }
    } catch (err: any) {
      console.error('Bin assignment error:', err.response?.data || err.message);
      const errorMsg = err.response?.data?.error ||
                       err.response?.data?.detail ||
                       err.response?.data?.message ||
                       (typeof err.response?.data === 'string' ? err.response.data : null) ||
                       err.message ||
                       'Failed to update feed bins';
      setCheckinError(`Bin update failed: ${errorMsg}`);
    } finally {
      setSavingBinAssignments(false);
    }
  };

  const resetCheckinReceipt = () => {
    setCheckinReceiptImage(null);
    setCheckinReceiptBase64(null);
    setCheckinScanResult(null);
    setCheckinError('');
    if (checkinFileInputRef.current) {
      checkinFileInputRef.current.value = '';
    }
  };

  // Receipt scanning functions
  const handleReceiptMemberSearch = async () => {
    if (!receiptMemberQuery.trim()) return;

    setReceiptMemberLoading(true);
    setReceiptMemberError('');
    setReceiptMember(null);

    try {
      const response = await portalAPI.get(`/portal/bridge-club/lookup/?q=${encodeURIComponent(receiptMemberQuery)}`);
      setReceiptMember(response.data);
    } catch (err: any) {
      setReceiptMemberError(err.response?.data?.error || 'Member not found');
    } finally {
      setReceiptMemberLoading(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setReceiptError('Please select an image file');
      return;
    }

    try {
      const base64 = await compressReceiptImage(file);
      setReceiptImage(`data:image/jpeg;base64,${base64}`);
      setReceiptImageBase64(base64);
      await scanReceiptImage(base64);
    } catch (err) {
      console.error('Receipt compression failed:', err);
      setReceiptError('Could not process that photo. Try a different image.');
    }
  };

  const scanReceiptImage = async (base64: string) => {
    setScanningReceipt(true);
    setReceiptError('');
    setScanResult(null);

    try {
      const response = await portalAPI.post('/portal/bridge-club/scan_receipt/', {
        receipt_image: base64,
        member_id: receiptMember?.id,
        auto_process: true,  // Enable smart scanning with auto redemption detection
      });

      setScanResult(response.data);

      // If auto-processed, also refresh data to reflect changes
      if (response.data.auto_processed) {
        loadData();
      }
    } catch (err: any) {
      setReceiptError(err.response?.data?.error || 'Failed to scan receipt. Please try again or enter manually.');
      // Set a placeholder result for manual entry
      setScanResult({
        items: [{ name: '', quantity: 1, price: 0, is_red_bridge: false, loyalty_bags: 0 }],
        total: 0,
        red_bridge_total: 0,
        total_loyalty_bags: 0,
      });
    } finally {
      setScanningReceipt(false);
    }
  };

  const handleReceiptItemChange = (index: number, field: keyof ReceiptItem, value: any) => {
    if (!scanResult) return;

    const newItems = [...scanResult.items];
    newItems[index] = { ...newItems[index], [field]: value };

    // Recalculate totals
    const redBridgeItems = newItems.filter(item => item.is_red_bridge);
    const total = newItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const redBridgeTotal = redBridgeItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const totalLoyaltyBags = redBridgeItems.reduce((sum, item) => sum + (item.loyalty_bags || item.quantity), 0);

    setScanResult({
      ...scanResult,
      items: newItems,
      total,
      red_bridge_total: redBridgeTotal,
      total_loyalty_bags: totalLoyaltyBags,
    });
  };

  const handleAddReceiptItem = () => {
    if (!scanResult) return;
    setScanResult({
      ...scanResult,
      items: [...scanResult.items, { name: '', quantity: 1, price: 0, is_red_bridge: false, loyalty_bags: 0 }],
    });
  };

  const handleRemoveReceiptItem = (index: number) => {
    if (!scanResult) return;
    const newItems = scanResult.items.filter((_, i) => i !== index);

    const redBridgeItems = newItems.filter(item => item.is_red_bridge);
    const total = newItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const redBridgeTotal = redBridgeItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const totalLoyaltyBags = redBridgeItems.reduce((sum, item) => sum + (item.loyalty_bags || item.quantity), 0);

    setScanResult({
      ...scanResult,
      items: newItems,
      total,
      red_bridge_total: redBridgeTotal,
      total_loyalty_bags: totalLoyaltyBags,
    });
  };

  const handleConfirmReceipt = async () => {
    if (!receiptMember || !scanResult) return;

    setSavingReceipt(true);
    setReceiptError('');

    try {
      const response = await portalAPI.post('/portal/bridge-club/record_purchase/', {
        member_id: receiptMember.id,
        red_bridge_bags: scanResult.total_loyalty_bags,
        total: scanResult.total,
        items: scanResult.items,
        receipt_image: receiptImageBase64,
      });

      const redBridgeBags = response.data.red_bridge_bags || 0;
      setReceiptSuccess(
        `Purchase recorded for ${receiptMember.first_name}! ` +
        `${redBridgeBags} Red Bridge bag${redBridgeBags !== 1 ? 's' : ''} added to loyalty (${response.data.member_loyalty_progress}/10)` +
        (response.data.member_reward_available ? ' - REWARD AVAILABLE!' : '')
      );

      // Reset receipt state
      setReceiptImage(null);
      setReceiptImageBase64(null);
      setScanResult(null);

      // Refresh stats
      loadData();
    } catch (err: any) {
      setReceiptError(err.response?.data?.error || 'Failed to save purchase');
    } finally {
      setSavingReceipt(false);
    }
  };

  const clearReceiptScan = () => {
    setReceiptMember(null);
    setReceiptMemberQuery('');
    setReceiptMemberError('');
    setReceiptImage(null);
    setReceiptImageBase64(null);
    setScanResult(null);
    setReceiptSuccess('');
    setReceiptError('');
  };

  const filteredMembers = members.filter(member => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      member.first_name.toLowerCase().includes(query) ||
      member.last_name.toLowerCase().includes(query) ||
      member.email.toLowerCase().includes(query) ||
      member.member_code.toLowerCase().includes(query) ||
      member.phone.includes(query)
    );
  });

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading Bridge Club data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <TutorialAutoStart pageKey="bridge-club" />

      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Bridge Club</h1>
          <p style={styles.subtitle}>Manage your store's Bridge Club members</p>
        </div>
        <TutorialHelpButton pageKey="bridge-club" />
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        <button
          style={{ ...styles.tab, ...(activeTab === 'dashboard' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('dashboard')}
        >
          Dashboard
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'checkin' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('checkin')}
          data-tutorial="checkin-tab"
        >
          Check-In
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'members' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('members')}
        >
          Members
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'promotions' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('promotions')}
        >
          📢 Promos
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'analytics' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('analytics')}
          data-tutorial="analytics-tab"
        >
          📊 Analytics
        </button>
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && stats && (
        <div style={styles.content}>
          <div style={styles.statsGrid} data-tutorial="bridge-stats">
            <div
              style={styles.statCardClickable}
              onClick={() => setActiveTab('members')}
            >
              <div style={styles.statValue}>{stats.total_members}</div>
              <div style={styles.statLabel}>Total Members</div>
              <div style={styles.statSub}>+{stats.new_members_this_month} this month</div>
              <div style={styles.clickHint}>Click to view →</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statValue}>{stats.total_red_bridge_bags}</div>
              <div style={styles.statLabel}>Red Bridge Bags Sold</div>
              <div style={styles.statSub}>+{stats.red_bridge_bags_this_month} this month</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statValue}>${stats.total_revenue.toFixed(0)}</div>
              <div style={styles.statLabel}>Total Revenue</div>
              <div style={styles.statSub}>+${stats.revenue_this_month.toFixed(0)} this month</div>
            </div>
            <div
              style={styles.statCardClickable}
              onClick={() => {
                loadRedemptionHistory();
                setShowRedemptionHistory(true);
              }}
            >
              <div style={styles.statValue}>{stats.total_rewards_redeemed}</div>
              <div style={styles.statLabel}>Rewards Redeemed</div>
              <div style={styles.statSub}>+{stats.rewards_redeemed_this_month} this month</div>
              <div style={styles.clickHint}>Click to view →</div>
            </div>
            {credits && credits.pending_total > 0 && (
              <div
                style={styles.creditStatCardClickable}
                onClick={() => setShowCreditsDetail(true)}
              >
                <div style={styles.creditStatValue}>${credits.pending_total.toFixed(2)}</div>
                <div style={styles.statLabel}>Pending Credits</div>
                <div style={styles.creditStatSub}>Applied to your next order</div>
                <div style={styles.clickHintGreen}>Click to view details →</div>
              </div>
            )}
          </div>

          {/* Redemption History Modal */}
          {showRedemptionHistory && (
            <div style={styles.modalOverlay}>
              <div style={styles.modalWide}>
                <div style={styles.modalHeader}>
                  <h3 style={styles.modalTitle}>Redemption History</h3>
                  <button onClick={() => setShowRedemptionHistory(false)} style={styles.modalClose}>×</button>
                </div>
                <div style={styles.modalBody}>
                  <div style={styles.historyStats}>
                    <span>This Month: <strong>{redemptionStats.this_month}</strong></span>
                    <span>All Time: <strong>{redemptionStats.total_all_time}</strong></span>
                  </div>
                  {redemptionHistory.length === 0 ? (
                    <p style={styles.noHistory}>No redemptions yet.</p>
                  ) : (
                    <div style={styles.historyList}>
                      {redemptionHistory.map((r) => (
                        <div key={r.id} style={styles.historyItem}>
                          <div style={styles.historyMember}>
                            <strong>{r.member_name}</strong>
                            <span style={styles.historyCode}>#{r.member_code}</span>
                          </div>
                          <div style={styles.historyProduct}>
                            {r.product_name} ({r.bag_size})
                          </div>
                          <div style={styles.historyMeta}>
                            <span style={styles.historyCost}>${r.wholesale_cost.toFixed(2)}</span>
                            <span style={styles.historyDate}>
                              {r.redeemed_at ? new Date(r.redeemed_at).toLocaleDateString() : 'N/A'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div style={styles.modalFooter}>
                  <button onClick={() => setShowRedemptionHistory(false)} style={styles.cancelBtn}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Credits Detail Modal */}
          {showCreditsDetail && credits && (
            <div style={styles.modalOverlay}>
              <div style={styles.modalWide}>
                <div style={styles.modalHeader}>
                  <h3 style={styles.modalTitle}>Pending Credits</h3>
                  <button onClick={() => setShowCreditsDetail(false)} style={styles.modalClose}>×</button>
                </div>
                <div style={styles.modalBody}>
                  <div style={styles.creditsSummary}>
                    <div style={styles.creditsBig}>
                      ${credits.pending_total.toFixed(2)}
                    </div>
                    <div style={styles.creditsLabel}>
                      {credits.pending_count} pending credit{credits.pending_count !== 1 ? 's' : ''}
                    </div>
                    <div style={styles.creditsNote}>
                      These credits will be applied to your next wholesale order
                    </div>
                  </div>
                  {credits.credits.length > 0 && (
                    <div style={styles.creditsList}>
                      <h4 style={styles.creditsListTitle}>Recent Credits</h4>
                      {credits.credits.map((c) => (
                        <div key={c.id} style={styles.creditItem}>
                          <div style={styles.creditDescription}>{c.description}</div>
                          <div style={styles.creditMeta}>
                            <span style={styles.creditAmount}>${c.amount.toFixed(2)}</span>
                            <span style={{
                              ...styles.creditStatus,
                              color: c.status === 'pending' ? '#f59e0b' : '#22c55e',
                            }}>
                              {c.status}
                            </span>
                            <span style={styles.creditDate}>
                              {new Date(c.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div style={styles.modalFooter}>
                  <button onClick={() => setShowCreditsDetail(false)} style={styles.cancelBtn}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Quick Actions</h2>
            <div style={styles.actionsRow}>
              <button
                style={styles.actionButton}
                onClick={() => setActiveTab('checkin')}
              >
                Check In Member
              </button>
              <button
                style={styles.actionButtonSecondary}
                onClick={() => setActiveTab('members')}
              >
                View All Members
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Check-In Tab */}
      {activeTab === 'checkin' && (
        <div style={styles.content}>
          <div style={styles.checkinCard}>
            <h2 style={styles.sectionTitle}>Member Check-In</h2>

            {/* QR Scanner Section */}
            <div style={styles.scannerSection}>
              {scannerEnabled ? (
                <>
                  <div id="qr-reader" style={styles.qrScanner}></div>
                  {scannerError && <div style={styles.scannerError}>{scannerError}</div>}
                  <button onClick={handleDisableScanner} style={styles.scannerToggleBtn}>
                    Close Scanner
                  </button>
                </>
              ) : (
                <button onClick={handleEnableScanner} style={styles.scanQrBtn} data-tutorial="scan-qr-btn">
                  <span style={styles.scanIcon}>📷</span> Scan QR Code
                </button>
              )}
            </div>

            <div style={styles.divider}>— or look up manually —</div>

            <div style={styles.searchRow} data-tutorial="member-search">
              <input
                type="text"
                value={checkinQuery}
                onChange={(e) => setCheckinQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCheckinSearch()}
                placeholder="Member code, email, or phone..."
                style={styles.searchInput}
              />
              <button
                onClick={() => handleCheckinSearch()}
                disabled={checkinLoading || !checkinQuery.trim()}
                style={styles.searchButton}
              >
                {checkinLoading ? 'Searching...' : 'Look Up'}
              </button>
              {checkinResult && (
                <button onClick={clearCheckin} style={styles.clearButton}>
                  Clear
                </button>
              )}
            </div>

            {checkinError && (
              <div style={styles.errorBox}>
                <div>{checkinError}</div>
                {checkinScanSuggestions.length > 0 && (
                  <ul style={{ margin: '0.5rem 0 0 1.25rem', padding: 0, fontSize: '0.9rem', lineHeight: 1.5 }}>
                    {checkinScanSuggestions.map((tip, i) => <li key={i}>{tip}</li>)}
                  </ul>
                )}
              </div>
            )}
            {checkinSuccess && <div style={styles.successBox}>{checkinSuccess}</div>}

            {checkinResult && !showReceiptInCheckin && (
              <div style={styles.memberCard}>
                <div style={styles.memberHeader}>
                  <div>
                    <div style={styles.memberName}>
                      {checkinResult.first_name} {checkinResult.last_name}
                    </div>
                    <div style={styles.memberCode}>#{checkinResult.member_code}</div>
                  </div>
                  {(checkinResult.available_rewards || 0) > 0 && (
                    <div style={styles.rewardBadge}>{checkinResult.available_rewards} FREE!</div>
                  )}
                </div>

                {/* Available Rewards Banner */}
                {(checkinResult.available_rewards || 0) > 0 && (
                  <div style={styles.rewardsBanner}>
                    <span style={styles.rewardsBannerIcon}>🎁</span>
                    <div style={styles.rewardsBannerText}>
                      <span style={styles.rewardsBannerCount}>{checkinResult.available_rewards}</span>
                      <span style={styles.rewardsBannerLabel}>
                        FREE {checkinResult.available_rewards === 1 ? 'BAG' : 'BAGS'} AVAILABLE!
                      </span>
                    </div>
                    <span style={styles.rewardsBannerHint}>Tap "Redeem Reward" below</span>
                  </div>
                )}

                {/* Partner promotions this member can redeem now (Phase 2). Confirm-
                    to-apply, never silent. Red Bridge records the one-time redemption;
                    the cashier keys the actual discount on their OWN register. */}
                {(checkinResult.eligible_promotions || []).length > 0 && (
                  <div style={{ margin: '8px 0' }}>
                    {(checkinResult.eligible_promotions || []).map(promo => (
                      <div key={promo.id} style={{ border: '2px solid #16a34a', background: '#f0fdf4', borderRadius: 10, padding: '12px 14px', marginBottom: 8 }}>
                        <div style={{ fontWeight: 700, color: '#166534' }}>🎉 {promo.title}</div>
                        {promo.message && <div style={{ fontSize: 13, color: '#166534', marginTop: 2 }}>{promo.message}</div>}
                        <div style={{ fontSize: 12, color: '#3f6212', marginTop: 4 }}>
                          Apply {promo.percent}% off the {promo.applies_to} — <strong>key the discount on your own register</strong>. This just records the one-time redemption.
                        </div>
                        {promoApplyingId === promo.id ? (
                          <div style={{ marginTop: 8 }}>
                            <label style={{ fontSize: 12, color: '#3f6212', display: 'block', marginBottom: 4 }}>
                              2nd-bag price (optional — for your records)
                            </label>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              <input
                                type="number" step="0.01" min="0" value={promoDiscount}
                                onChange={e => setPromoDiscount(e.target.value)} placeholder="e.g. 24.99"
                                style={{ padding: '6px 8px', border: '1px solid #86efac', borderRadius: 6, width: 120 }}
                              />
                              <button
                                disabled={promoWorking}
                                onClick={() => handleApplyPromotion(promo, promoDiscount || undefined)}
                                style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontWeight: 600, cursor: 'pointer' }}
                              >
                                {promoWorking ? 'Recording…' : 'Record redemption'}
                              </button>
                              <button
                                disabled={promoWorking}
                                onClick={() => { setPromoApplyingId(null); setPromoDiscount(''); }}
                                style={{ background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 14px', cursor: 'pointer' }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            <button
                              onClick={() => { setPromoApplyingId(promo.id); setPromoMessage(null); }}
                              style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 16px', fontWeight: 700, cursor: 'pointer' }}
                            >
                              Apply
                            </button>
                            <button
                              onClick={() => setCheckinResult({ ...checkinResult, eligible_promotions: (checkinResult.eligible_promotions || []).filter(p => p.id !== promo.id) })}
                              style={{ background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 16px', cursor: 'pointer' }}
                            >
                              Not now
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                    {promoMessage && (
                      <div style={{ padding: '8px 12px', borderRadius: 8, fontSize: 13,
                        background: promoMessage.type === 'success' ? '#dcfce7' : '#fef2f2',
                        color: promoMessage.type === 'success' ? '#166534' : '#b91c1c' }}>
                        {promoMessage.text}
                      </div>
                    )}
                  </div>
                )}

                <div style={styles.memberDetails}>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Email:</span>
                    <span>{checkinResult.email}</span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Phone:</span>
                    <span>{checkinResult.phone || 'N/A'}</span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Home Store:</span>
                    <span>{checkinResult.linked_store_name || 'None'}</span>
                  </div>
                </div>

                {/* Loyalty Progress */}
                <div style={styles.loyaltySection}>
                  <div style={styles.loyaltyLabel}>
                    Loyalty Progress: {checkinResult.loyalty_progress}/10
                  </div>
                  <div style={styles.loyaltyDots}>
                    {[...Array(10)].map((_, i) => (
                      <span
                        key={i}
                        style={{
                          ...styles.loyaltyDot,
                          ...(i < checkinResult.loyalty_progress ? styles.loyaltyDotFilled : {}),
                        }}
                      />
                    ))}
                  </div>
                  {checkinResult.bags_until_reward > 0 && (
                    <div style={styles.loyaltySub}>
                      {checkinResult.bags_until_reward} more bags until reward
                    </div>
                  )}
                </div>

                {/* Animals */}
                {checkinResult.animals.length > 0 && (
                  <div style={styles.animalsSection}>
                    <div style={styles.animalsLabel}>Animals:</div>
                    <div style={styles.animalsList}>
                      {checkinResult.animals.map((animal) => (
                        <span key={animal.id} style={styles.animalChip}>
                          {animal.name || animal.category} ({animal.count})
                          {animal.is_red_bridge && <span style={styles.rbTag}>RB</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Feed Bins Status - Shows all bins with visual status */}
                {checkinFeedBins.length > 0 && (
                  <div style={styles.feedBinsSection}>
                    <div style={styles.feedBinsHeader}>
                      <span style={styles.feedBinsLabel}>Feed Bins:</span>
                      {checkinFeedBins.some(b => b.status === 'critical' || b.status === 'empty' || b.status === 'low') && (
                        <span style={styles.feedBinsAlert}>Needs Feed!</span>
                      )}
                    </div>
                    <div style={styles.feedBinsGrid}>
                      {checkinFeedBins.map((bin) => {
                        const isUrgent = bin.status === 'critical' || bin.status === 'empty';
                        const isLow = bin.status === 'low';
                        const statusColor = isUrgent ? '#dc2626' : isLow ? '#f59e0b' : bin.percent_full > 50 ? '#10b981' : '#3b82f6';
                        const statusLabel = bin.status === 'empty' ? 'EMPTY' : bin.status === 'critical' ? 'LOW' : bin.status === 'low' ? 'LOW' : bin.percent_full > 50 ? 'GOOD' : 'OK';

                        return (
                          <div
                            key={bin.id}
                            style={{
                              ...styles.feedBinCard,
                              borderColor: isUrgent ? '#dc2626' : isLow ? '#f59e0b' : '#e5e7eb',
                              backgroundColor: isUrgent ? '#fef2f2' : isLow ? '#fffbeb' : '#ffffff',
                            }}
                          >
                            <div style={styles.feedBinTop}>
                              <span style={styles.feedBinIcon}>{bin.category_icon || '🛢️'}</span>
                              <span style={styles.feedBinName}>{bin.name}</span>
                            </div>
                            <div style={styles.feedBinMiddle}>
                              <span style={{ ...styles.feedBinPercent, color: statusColor }}>
                                {Math.round(bin.percent_full)}%
                              </span>
                              <span style={{ ...styles.feedBinBadge, backgroundColor: statusColor }}>
                                {statusLabel}
                              </span>
                            </div>
                            <div style={styles.feedBinBottom}>
                              {bin.days_remaining !== null ? (
                                <span style={{ color: bin.days_remaining <= 3 ? '#dc2626' : '#6b7280' }}>
                                  {bin.days_remaining} days
                                </span>
                              ) : (
                                <span style={{ color: '#9ca3af' }}>—</span>
                              )}
                            </div>
                            {isUrgent && (
                              <div style={styles.feedBinSuggestion}>
                                Suggest: {bin.product_name || 'feed'}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Trip Planner Calculator */}
                    <div style={styles.tripPlannerToggle}>
                      <button
                        onClick={() => setShowTripPlanner(!showTripPlanner)}
                        style={styles.tripPlannerToggleBtn}
                      >
                        {showTripPlanner ? '▼' : '▶'} Trip Planner Calculator
                      </button>
                    </div>

                    {showTripPlanner && (
                      <div style={styles.tripPlannerSection}>
                        <div style={styles.tripPlannerQuestion}>
                          How long until their next visit?
                        </div>
                        <div style={styles.tripPlannerOptions}>
                          {[7, 14, 21, 30].map((days) => (
                            <button
                              key={days}
                              onClick={() => setTripPlannerDays(days)}
                              style={{
                                ...styles.tripPlannerOption,
                                ...(tripPlannerDays === days ? styles.tripPlannerOptionActive : {}),
                              }}
                            >
                              {days === 7 ? '1 week' : days === 14 ? '2 weeks' : days === 21 ? '3 weeks' : '1 month'}
                            </button>
                          ))}
                        </div>

                        <div style={styles.tripPlannerResults}>
                          <div style={styles.tripPlannerResultsHeader}>
                            To last {tripPlannerDays === 7 ? '1 week' : tripPlannerDays === 14 ? '2 weeks' : tripPlannerDays === 21 ? '3 weeks' : '1 month'}, recommend:
                          </div>
                          {checkinFeedBins.map((bin) => {
                            const totalNeeded = (bin.daily_consumption || 0) * tripPlannerDays;
                            const shortfall = totalNeeded - (bin.current_lbs || 0);
                            const bagsNeeded = shortfall > 0 ? Math.ceil(shortfall / (bin.default_bag_lbs || 40)) : 0;
                            const bagSize = bin.default_bag_lbs || 40;

                            return (
                              <div key={bin.id} style={styles.tripPlannerItem}>
                                <div style={styles.tripPlannerItemHeader}>
                                  <span>{bin.category_icon} {bin.name}</span>
                                  {bagsNeeded > 0 ? (
                                    <span style={styles.tripPlannerBagsNeeded}>Need: {bagsNeeded} bag{bagsNeeded !== 1 ? 's' : ''}</span>
                                  ) : (
                                    <span style={styles.tripPlannerBagsOk}>Has enough</span>
                                  )}
                                </div>
                                <div style={styles.tripPlannerItemDetails}>
                                  <div>{bin.product_name || bin.name} ({bagSize}lb)</div>
                                  <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                    Current: {Math.round(bin.current_lbs || 0)} lbs •
                                    Usage: {(bin.daily_consumption || 0).toFixed(1)} lbs/day × {tripPlannerDays} days = {Math.round(totalNeeded)} lbs needed
                                  </div>
                                </div>
                              </div>
                            );
                          })}

                          {/* Summary */}
                          {(() => {
                            const recommendations = checkinFeedBins
                              .map((bin) => {
                                const totalNeeded = (bin.daily_consumption || 0) * tripPlannerDays;
                                const shortfall = totalNeeded - (bin.current_lbs || 0);
                                const bagsNeeded = shortfall > 0 ? Math.ceil(shortfall / (bin.default_bag_lbs || 40)) : 0;
                                return { bin, bagsNeeded };
                              })
                              .filter(r => r.bagsNeeded > 0);

                            if (recommendations.length === 0) {
                              return (
                                <div style={styles.tripPlannerSummary}>
                                  <strong>Customer has enough feed for {tripPlannerDays === 7 ? '1 week' : tripPlannerDays === 14 ? '2 weeks' : tripPlannerDays === 21 ? '3 weeks' : '1 month'}!</strong>
                                </div>
                              );
                            }

                            return (
                              <div style={styles.tripPlannerSummary}>
                                <strong>TOTAL RECOMMENDATION:</strong>
                                {recommendations.map(({ bin, bagsNeeded }) => (
                                  <div key={bin.id}>
                                    • {bagsNeeded}x {bin.product_name || bin.name} ({bin.default_bag_lbs || 40}lb)
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Record Purchase - Manual Entry (primary, above the fold) */}
                <div style={styles.purchaseSection} data-tutorial="record-purchase">
                  <div style={styles.purchaseTitle}>Record Purchase</div>

                  {/* Receipt info */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 160, flex: '1 1 160px' }}>
                      <label style={styles.purchaseLabel}>Journal Number</label>
                      <input
                        type="text"
                        value={purchaseJournal}
                        onChange={(e) => setPurchaseJournal(e.target.value)}
                        style={styles.purchaseInput}
                        placeholder="from receipt JRNL#"
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 130, flex: '1 1 130px' }}>
                      <label style={styles.purchaseLabel}>Date</label>
                      <input type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} style={styles.purchaseInput} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 110, flex: '1 1 110px' }}>
                      <label style={styles.purchaseLabel}>Time</label>
                      <input type="time" value={purchaseTime} onChange={(e) => setPurchaseTime(e.target.value)} style={styles.purchaseInput} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 120, flex: '1 1 120px' }}>
                      <label style={styles.purchaseLabel}>Cashier</label>
                      <input type="text" value={purchaseCashier} onChange={(e) => setPurchaseCashier(e.target.value)} style={styles.purchaseInput} placeholder="optional" />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 90, flex: '1 1 90px' }}>
                      <label style={styles.purchaseLabel}>Register</label>
                      <input type="text" value={purchaseRegister} onChange={(e) => setPurchaseRegister(e.target.value)} style={styles.purchaseInput} placeholder="optional" />
                    </div>
                  </div>

                  {/* Products purchased */}
                  <label style={styles.purchaseLabel}>Products Purchased</label>
                  {purchaseLineItems.map((li, idx) => (
                    <div key={idx} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                      <select
                        value={li.product_id}
                        onChange={(e) => updatePurchaseLineItem(idx, 'product_id', e.target.value)}
                        style={{ ...styles.purchaseInput, flex: '2 1 180px', minHeight: 44 }}
                      >
                        <option value="">Select product…</option>
                        {bcProducts.map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                      <input
                        type="number" min="1" inputMode="numeric"
                        value={li.quantity}
                        onChange={(e) => updatePurchaseLineItem(idx, 'quantity', e.target.value)}
                        style={{ ...styles.purchaseInput, width: 70, flex: '0 0 70px' }}
                        placeholder="Qty"
                        aria-label="Quantity"
                      />
                      <input
                        type="number" min="0" step="0.01" inputMode="decimal"
                        value={li.unit_price}
                        onChange={(e) => updatePurchaseLineItem(idx, 'unit_price', e.target.value)}
                        style={{ ...styles.purchaseInput, width: 100, flex: '0 0 100px' }}
                        placeholder="Customer paid (each)"
                        aria-label="Customer paid (each)"
                        title="Enter the price the customer paid — for sales reporting only. Does not affect punch card or store credit."
                      />
                      {purchaseLineItems.length > 1 && (
                        <button
                          onClick={() => removePurchaseLineItem(idx)}
                          style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 22, cursor: 'pointer', minWidth: 44, minHeight: 44 }}
                          aria-label="Remove product"
                        >×</button>
                      )}
                    </div>
                  ))}
                  <div style={{ fontSize: 12, color: '#6b7280', margin: '0 0 8px' }}>
                    Enter the price the customer paid — for sales reporting only. Does not affect punch card or store credit.
                  </div>
                  <button
                    onClick={addPurchaseLineItem}
                    style={{ background: 'none', border: '1px dashed #94a3b8', color: '#475569', borderRadius: 8, padding: '8px 12px', minHeight: 44, cursor: 'pointer', marginBottom: 12 }}
                  >
                    + Add Another Product
                  </button>

                  {/* Free bag + total */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 12px', minHeight: 44, cursor: 'pointer' }}>
                    <input type="checkbox" checked={purchaseFreeBag} onChange={(e) => setPurchaseFreeBag(e.target.checked)} style={{ width: 20, height: 20 }} />
                    <span>This purchase included a free bag redemption</span>
                  </label>

                  <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 12 }}>
                    Total: ${manualPurchaseTotal().toFixed(2)}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label style={styles.purchaseLabel}>Notes (optional)</label>
                    <input type="text" value={purchaseNotes} onChange={(e) => setPurchaseNotes(e.target.value)} style={styles.purchaseInput} />
                  </div>

                  <button
                    onClick={handleRecordPurchase}
                    disabled={recordingPurchase}
                    style={{ ...styles.recordButton, width: '100%', marginTop: 12 }}
                  >
                    {recordingPurchase ? 'Recording…' : 'Record Purchase'}
                  </button>

                  {/* Secondary: scan receipt */}
                  <button
                    onClick={() => setShowReceiptInCheckin(true)}
                    style={{ background: 'none', border: 'none', color: '#508590', textDecoration: 'underline', cursor: 'pointer', marginTop: 12, minHeight: 44, fontSize: 14 }}
                  >
                    📷 Or scan receipt instead
                  </button>
                </div>

                {/* Hard duplicate (journal already recorded) */}
                {dupBlock && (
                  <div style={styles.modalOverlay}>
                    <div style={styles.modal}>
                      <div style={styles.modalHeader}>
                        <h3 style={styles.modalTitle}>Already Recorded</h3>
                        <button onClick={() => setDupBlock(null)} style={styles.modalClose}>×</button>
                      </div>
                      <div style={styles.modalBody}>
                        <p>{dupBlock.message}</p>
                        <button onClick={() => setDupBlock(null)} style={{ ...styles.recordButton, width: '100%', marginTop: 12 }}>
                          OK
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Soft duplicate warning — confirm to record anyway */}
                {dupWarning && (
                  <div style={styles.modalOverlay}>
                    <div style={styles.modal}>
                      <div style={styles.modalHeader}>
                        <h3 style={styles.modalTitle}>Possible Duplicate</h3>
                        <button onClick={() => setDupWarning(null)} style={styles.modalClose}>×</button>
                      </div>
                      <div style={styles.modalBody}>
                        <p>{dupWarning.message}</p>
                        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                          <button onClick={() => setDupWarning(null)} style={{ ...styles.purchaseInput, flex: 1, cursor: 'pointer', minHeight: 44 }}>
                            Cancel
                          </button>
                          <button
                            onClick={() => { setDupWarning(null); submitManualPurchase(true); }}
                            style={{ ...styles.recordButton, flex: 1 }}
                          >
                            Record Anyway
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Redeem Reward */}
                {(checkinResult.available_rewards || 0) > 0 && (
                  <div style={styles.redeemSection}>
                    <button onClick={openRedeemModal} style={styles.redeemButton} data-tutorial="redeem-btn">
                      Redeem 1 FREE Bag ({checkinResult.available_rewards} available)
                    </button>
                  </div>
                )}

                {/* Feed Selection Modal */}
                {showRedeemModal && (
                  <div style={styles.modalOverlay}>
                    <div style={styles.modal}>
                      <div style={styles.modalHeader}>
                        <h3 style={styles.modalTitle}>Select Feed Product</h3>
                        <button onClick={closeRedeemModal} style={styles.modalClose}>×</button>
                      </div>
                      <div style={styles.modalBody}>
                        <p style={styles.modalSubtitle}>
                          What feed is {checkinResult.first_name} receiving?
                        </p>
                        <div style={styles.productList}>
                          {Object.keys(groupedProducts).length === 0 && feedProducts.length === 0 ? (
                            <p style={styles.noProducts}>No products available. Contact admin to set up Feed Availability.</p>
                          ) : Object.keys(groupedProducts).length > 0 ? (
                            // Show grouped by animal type
                            Object.entries(groupedProducts).map(([animalType, products]) => (
                              <div key={animalType}>
                                <div style={styles.animalTypeHeader}>
                                  {animalType.charAt(0) + animalType.slice(1).toLowerCase()}
                                </div>
                                {products.map((product) => (
                                  <div
                                    key={`${product.id}-${product.form || 'mash'}`}
                                    style={{
                                      ...styles.productOption,
                                      ...(selectedProduct?.id === product.id && selectedProduct?.form === product.form ? styles.productOptionSelected : {}),
                                    }}
                                    onClick={() => setSelectedProduct(product)}
                                  >
                                    <div style={styles.productName}>
                                      {product.name}
                                      <span style={styles.formBadge}>
                                        {product.form === 'pellet' ? ' Pellet' : ' Mash'}
                                      </span>
                                    </div>
                                    <div style={styles.productDetails}>
                                      <span>{product.bag_size}</span>
                                      <span style={styles.productPrice}>${product.wholesale_price.toFixed(2)} credit</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ))
                          ) : (
                            // Fallback to flat list
                            feedProducts.map((product) => (
                              <div
                                key={`${product.id}-${product.form || 'mash'}`}
                                style={{
                                  ...styles.productOption,
                                  ...(selectedProduct?.id === product.id && selectedProduct?.form === product.form ? styles.productOptionSelected : {}),
                                }}
                                onClick={() => setSelectedProduct(product)}
                              >
                                <div style={styles.productName}>
                                  {product.name}
                                  <span style={styles.formBadge}>
                                    {product.form === 'pellet' ? ' Pellet' : ' Mash'}
                                  </span>
                                </div>
                                <div style={styles.productDetails}>
                                  <span>{product.bag_size}</span>
                                  <span style={styles.productPrice}>${product.wholesale_price.toFixed(2)} credit</span>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                        {selectedProduct && (
                          <div style={styles.selectedSummary}>
                            <strong>Selected:</strong> {selectedProduct.name} - {selectedProduct.form === 'pellet' ? 'Pellet' : 'Mash'} ({selectedProduct.bag_size})
                            <br />
                            <strong>Store Credit:</strong> ${selectedProduct.wholesale_price.toFixed(2)}
                          </div>
                        )}
                      </div>
                      <div style={styles.modalFooter}>
                        <button onClick={closeRedeemModal} style={styles.cancelBtn}>
                          Cancel
                        </button>
                        <button
                          onClick={handleRedeemReward}
                          disabled={!selectedProduct || redeemingReward}
                          style={{
                            ...styles.confirmBtn,
                            opacity: !selectedProduct ? 0.5 : 1,
                          }}
                        >
                          {redeemingReward ? 'Processing...' : 'Confirm Redemption'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Bin Assignment Prompt Modal - Smart Matching */}
                {showBinAssignPrompt && checkinFeedBins.length > 0 && (
                  <div style={styles.modalOverlay}>
                    <div style={styles.modal}>
                      <div style={styles.modalHeader}>
                        <h3 style={styles.modalTitle}>Add to Feed Bins?</h3>
                        <button onClick={() => setShowBinAssignPrompt(false)} style={styles.modalClose}>×</button>
                      </div>
                      <div style={styles.modalBody}>
                        {(() => {
                          // Group ALL purchased items by their detected category (not just Red Bridge)
                          // This allows customers to track competitor feeds in their bins too
                          const allFeedItems = lastPurchaseItems;
                          const itemsByCategory: Record<string, { items: typeof allFeedItems; bins: FeedBin[] }> = {};

                          allFeedItems.forEach(item => {
                            const categories = detectFeedCategory(item.name);
                            const matchingBins = checkinFeedBins.filter(bin =>
                              categories.includes(bin.category) || categories.includes('other')
                            );

                            // Use first detected category as key
                            const catKey = categories[0] || 'other';
                            if (!itemsByCategory[catKey]) {
                              itemsByCategory[catKey] = { items: [], bins: matchingBins };
                            }
                            itemsByCategory[catKey].items.push(item);
                          });

                          const categoryLabels: Record<string, string> = {
                            poultry: '🐔 Chicken/Poultry Feed',
                            dog: '🐕 Dog Food',
                            cat: '🐱 Cat Food',
                            horse: '🐴 Horse Feed',
                            goat: '🐐 Goat Feed',
                            cattle: '🐄 Cattle Feed',
                            sheep: '🐑 Sheep Feed',
                            swine: '🐷 Swine Feed',
                            rabbit: '🐰 Rabbit Feed',
                            other: '📦 Other Feed',
                          };

                          return Object.entries(itemsByCategory).map(([category, { items, bins }]) => {
                            const totalBags = items.reduce((sum, i) => sum + (i.quantity || 0), 0);
                            const itemNames = Array.from(new Set(items.map(i => i.name))).join(', ');

                            // If no matching bins, show a message
                            if (bins.length === 0) {
                              return (
                                <div key={category} style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                                  <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                                    {categoryLabels[category] || category}
                                  </div>
                                  <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                                    {itemNames} ({totalBags} bag{totalBags !== 1 ? 's' : ''})
                                  </div>
                                  <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginTop: '0.5rem', fontStyle: 'italic' }}>
                                    No matching bins set up for this feed type
                                  </div>
                                </div>
                              );
                            }

                            return (
                              <div key={category} style={{ marginBottom: '1.25rem' }}>
                                <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                                  {categoryLabels[category] || category}
                                </div>
                                <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.75rem' }}>
                                  {itemNames} ({totalBags} bag{totalBags !== 1 ? 's' : ''})
                                </div>
                                {bins.map(bin => (
                                  <div
                                    key={bin.id}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.75rem',
                                      padding: '0.75rem',
                                      marginBottom: '0.5rem',
                                      backgroundColor: binAssignments[bin.id]?.selected ? '#f0fdf4' : '#f9fafb',
                                      border: binAssignments[bin.id]?.selected ? '2px solid #22c55e' : '1px solid #e5e7eb',
                                      borderRadius: '8px',
                                      cursor: 'pointer',
                                    }}
                                    onClick={() => {
                                      setBinAssignments(prev => ({
                                        ...prev,
                                        [bin.id]: {
                                          ...prev[bin.id],
                                          selected: !prev[bin.id]?.selected,
                                          quantity: prev[bin.id]?.quantity || totalBags,
                                        },
                                      }));
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={binAssignments[bin.id]?.selected || false}
                                      onChange={() => {}}
                                      style={{ width: '20px', height: '20px' }}
                                    />
                                    <span style={{ fontSize: '1.5rem' }}>{bin.category_icon || '🛢️'}</span>
                                    <div style={{ flex: 1 }}>
                                      <div style={{ fontWeight: 600 }}>{bin.name}</div>
                                      <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                                        {bin.product_name} • {Math.round(bin.percent_full)}% full
                                      </div>
                                    </div>
                                    {binAssignments[bin.id]?.selected && (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setBinAssignments(prev => ({
                                              ...prev,
                                              [bin.id]: {
                                                ...prev[bin.id],
                                                quantity: Math.max(1, (prev[bin.id]?.quantity || 1) - 1),
                                              },
                                            }));
                                          }}
                                          style={{
                                            width: '28px',
                                            height: '28px',
                                            borderRadius: '4px',
                                            border: '1px solid #d1d5db',
                                            background: 'white',
                                            cursor: 'pointer',
                                          }}
                                        >
                                          -
                                        </button>
                                        <span style={{ minWidth: '30px', textAlign: 'center', fontWeight: 600 }}>
                                          {binAssignments[bin.id]?.quantity || 1}
                                        </span>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setBinAssignments(prev => ({
                                              ...prev,
                                              [bin.id]: {
                                                ...prev[bin.id],
                                                quantity: (prev[bin.id]?.quantity || 1) + 1,
                                              },
                                            }));
                                          }}
                                          style={{
                                            width: '28px',
                                            height: '28px',
                                            borderRadius: '4px',
                                            border: '1px solid #d1d5db',
                                            background: 'white',
                                            cursor: 'pointer',
                                          }}
                                        >
                                          +
                                        </button>
                                        <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>bags</span>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            );
                          });
                        })()}

                        {Object.values(binAssignments).some(a => a.selected) && (
                          <div style={{
                            padding: '0.75rem',
                            backgroundColor: '#fef9c3',
                            borderRadius: '8px',
                            fontSize: '0.9rem',
                            marginTop: '1rem',
                          }}>
                            <strong>Will add:</strong>
                            {checkinFeedBins
                              .filter(bin => binAssignments[bin.id]?.selected)
                              .map(bin => (
                                <div key={bin.id} style={{ marginTop: '0.25rem' }}>
                                  • {binAssignments[bin.id]?.quantity || 1} bag{(binAssignments[bin.id]?.quantity || 1) !== 1 ? 's' : ''} ({(binAssignments[bin.id]?.quantity || 1) * (bin.default_bag_lbs || 40)} lbs) to {bin.name}
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                      <div style={styles.modalFooter}>
                        <button
                          onClick={() => setShowBinAssignPrompt(false)}
                          style={styles.cancelBtn}
                        >
                          Skip
                        </button>
                        <button
                          onClick={handleBinAssignmentSubmit}
                          disabled={savingBinAssignments || !Object.values(binAssignments).some(a => a.selected)}
                          style={{
                            ...styles.confirmBtn,
                            opacity: !Object.values(binAssignments).some(a => a.selected) ? 0.5 : 1,
                          }}
                        >
                          {savingBinAssignments ? 'Saving...' : 'Add to Bins'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Inline Receipt Scanner during Check-In */}
            {checkinResult && showReceiptInCheckin && (
              <div style={styles.memberCard}>
                <div style={styles.receiptScannerHeader}>
                  <h3 style={styles.receiptScannerTitle}>
                    Scan Receipt for {checkinResult.first_name}
                  </h3>
                  <button
                    onClick={() => {
                      setShowReceiptInCheckin(false);
                      resetCheckinReceipt();
                    }}
                    style={styles.closeReceiptBtn}
                  >
                    ✕
                  </button>
                </div>

                {/* Member Info Banner */}
                <div style={styles.memberBanner}>
                  <div>
                    <strong>{checkinResult.first_name} {checkinResult.last_name}</strong>
                    <span style={styles.memberCodeSmall}> #{checkinResult.member_code}</span>
                    <div style={styles.memberLoyaltySmall}>
                      Loyalty: {checkinResult.loyalty_progress}/10
                      {checkinResult.loyalty_reward_available && ' - Reward Available!'}
                    </div>
                  </div>
                </div>

                {/* Camera/Upload Receipt Area */}
                {!checkinReceiptImage && !checkinScanning && (
                  <>
                    {cameraActive ? (
                      <div style={styles.cameraContainer}>
                        <video
                          ref={videoRef}
                          autoPlay
                          playsInline
                          muted
                          style={styles.cameraVideo}
                          onClick={handleTapToFocus}
                        />
                        <div style={styles.tapToFocusHint}>Tap to focus</div>
                        <div style={styles.cameraControls}>
                          <button onClick={stopCamera} style={styles.cameraCancelBtn}>
                            Cancel
                          </button>
                          <button onClick={capturePhoto} style={styles.cameraCaptureBtn}>
                            📷 Capture
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={styles.receiptCaptureOptions}>
                        <button
                          onClick={startCamera}
                          style={styles.scanReceiptPrimaryBtn}
                        >
                          📷 Scan Receipt
                        </button>
                        {cameraError && (
                          <p style={styles.cameraErrorText}>{cameraError}</p>
                        )}
                        <div style={styles.orDivider}>— or —</div>
                        <button
                          onClick={() => checkinFileInputRef.current?.click()}
                          style={styles.uploadFromDeviceBtn}
                        >
                          Upload from device
                        </button>
                      </div>
                    )}
                  </>
                )}

                {/* Hidden canvas for capturing photo */}
                <canvas ref={canvasRef} style={{ display: 'none' }} />

                <input
                  ref={checkinFileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleCheckinReceiptSelect}
                  style={{ display: 'none' }}
                />

                {/* Scanning Indicator */}
                {checkinScanning && (
                  <div style={styles.scanningOverlay}>
                    <div style={styles.spinner}></div>
                    <p>Scanning receipt...</p>
                  </div>
                )}

                {/* Receipt Preview and Items */}
                {checkinReceiptImage && checkinScanResult && (
                  <>
                    <div style={styles.receiptPreview}>
                      <img src={checkinReceiptImage} alt="Receipt" style={styles.receiptImage} />
                      <button onClick={resetCheckinReceipt} style={styles.retakeBtn}>
                        Retake
                      </button>
                    </div>

                    {/* Items List */}
                    <div style={styles.itemsSection}>
                      <h4 style={styles.itemsSectionTitle}>Items</h4>
                      {checkinScanResult.items.map((item, index) => (
                        <div
                          key={index}
                          style={{
                            ...styles.itemRow,
                            backgroundColor: item.is_red_bridge ? '#f0fdf4' : '#f9fafb',
                            border: item.is_red_bridge ? '2px solid #22c55e' : '1px solid #e5e7eb',
                            borderRadius: '8px',
                            padding: '0.5rem',
                          }}
                        >
                          <div style={{ flex: 1, display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            {/* Product name - editable in case AI got it wrong */}
                            <input
                              type="text"
                              value={item.name}
                              onChange={(e) => handleCheckinItemChange(index, 'name', e.target.value)}
                              placeholder="Item name"
                              style={styles.itemNameInput}
                            />
                            {/* Quantity - editable */}
                            <input
                              type="text"
                              inputMode="numeric"
                              value={item.quantity}
                              onChange={(e) => handleCheckinItemChange(index, 'quantity', e.target.value)}
                              onBlur={(e) => {
                                const val = parseInt(e.target.value) || 1;
                                handleCheckinItemChange(index, 'quantity', Math.max(1, val));
                              }}
                              style={styles.itemQtyInput}
                              placeholder="Qty"
                            />
                            {/* RB badge - NOT toggleable, AI determines this */}
                            {item.is_red_bridge ? (
                              <span style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#dcfce7',
                                color: '#16a34a',
                                borderRadius: '4px',
                                fontSize: '0.8rem',
                                fontWeight: 600,
                              }}>
                                🎟️ Red Bridge
                              </span>
                            ) : (
                              <span style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#f3f4f6',
                                color: '#6b7280',
                                borderRadius: '4px',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                              }}>
                                📊 Other
                              </span>
                            )}
                            <button
                              onClick={() => handleCheckinRemoveItem(index)}
                              style={styles.removeItemBtn}
                            >
                              ×
                            </button>
                          </div>
                          {/* Loyalty indicator */}
                          <div style={{
                            width: '100%',
                            marginTop: '0.25rem',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            color: item.is_red_bridge ? '#16a34a' : '#6b7280',
                          }}>
                            {item.is_red_bridge ? (
                              <span>🎟️ Counts for loyalty ({item.quantity} bag{item.quantity !== 1 ? 's' : ''})</span>
                            ) : (
                              <span>Tracked for analytics only</span>
                            )}
                          </div>
                        </div>
                      ))}
                      <button onClick={handleCheckinAddItem} style={styles.addItemBtn}>
                        + Add Item
                      </button>

                      {/* Items Summary */}
                      {checkinScanResult.items.length > 0 && (
                        <div style={{
                          marginTop: '1rem',
                          padding: '0.75rem',
                          backgroundColor: '#fef9c3',
                          borderRadius: '8px',
                          border: '1px solid #fcd34d',
                        }}>
                          <div style={{ fontWeight: '600', marginBottom: '0.5rem', color: '#713f12' }}>
                            Purchase Summary
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                            <span style={{ color: '#16a34a' }}>
                              🎟️ Red Bridge bags: {checkinScanResult.items.filter(i => i.is_red_bridge).reduce((sum, i) => sum + (i.quantity || 0), 0)}
                            </span>
                            <span style={{ color: '#16a34a', fontWeight: '500' }}>
                              (counts toward free bag)
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', marginTop: '0.25rem' }}>
                            <span style={{ color: '#6b7280' }}>
                              📊 Other items: {checkinScanResult.items.filter(i => !i.is_red_bridge).reduce((sum, i) => sum + (i.quantity || 0), 0)}
                            </span>
                            <span style={{ color: '#6b7280' }}>
                              (tracked for records)
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Summary - Loyalty Bags */}
                    <div style={styles.receiptSummary}>
                      {(() => {
                        const rbItems = checkinScanResult.items.filter(i => i.is_red_bridge);
                        const rbBags = rbItems.reduce((sum, i) => sum + (i.quantity || 0), 0);
                        const otherItems = checkinScanResult.items.filter(i => !i.is_red_bridge);
                        const otherCount = otherItems.reduce((sum, i) => sum + (i.quantity || 0), 0);

                        return (
                          <>
                            <div style={styles.summaryRowHighlight}>
                              <span>🎟️ Red Bridge Bags:</span>
                              <strong style={{ color: '#16a34a' }}>+{rbBags} bags</strong>
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.25rem' }}>
                              Counts toward free bag reward
                            </div>
                            {otherCount > 0 && (
                              <div style={{ ...styles.summaryRow, marginTop: '0.75rem' }}>
                                <span>📊 Other Items:</span>
                                <span>{otherCount} item{otherCount !== 1 ? 's' : ''}</span>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>

                    {/* Detected Free Bag Redemptions */}
                    {checkinScanResult.auto_processed && (checkinScanResult.bags_redeemed ?? 0) > 0 && (
                      <div style={styles.redemptionAlert}>
                        <div style={styles.redemptionAlertHeader}>
                          🎉 Free Bag Redeemed!
                        </div>
                        <div style={styles.redemptionAlertContent}>
                          <p style={{ margin: '0 0 8px 0' }}>
                            Detected <strong>{checkinScanResult.bags_redeemed ?? 0}</strong> free bag redemption{(checkinScanResult.bags_redeemed ?? 0) > 1 ? 's' : ''} on this receipt.
                          </p>
                          {checkinScanResult.redemptions && checkinScanResult.redemptions.map((r: any, idx: number) => (
                            <div key={idx} style={styles.redemptionItem}>
                              <span style={styles.redemptionProduct}>{r.product_name} ({r.form_type})</span>
                              <div style={styles.redemptionDetails}>
                                <span style={styles.redemptionMsrp}>Customer saved: ${r.msrp_discount?.toFixed(2) || r.msrp?.toFixed(2)}</span>
                                <span style={styles.redemptionCredit}>Store credit: ${r.wholesale_credit?.toFixed(2)}</span>
                              </div>
                            </div>
                          ))}
                          {(checkinScanResult.total_store_credit ?? 0) > 0 && (
                            <div style={{ marginTop: '8px', fontWeight: 600, color: '#059669' }}>
                              Total Store Credit: ${checkinScanResult.total_store_credit?.toFixed(2)}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Unmatched Discounts Warning */}
                    {checkinScanResult.auto_processed && (checkinScanResult.discounts_detected?.length ?? 0) > 0 && (checkinScanResult.bags_redeemed ?? 0) === 0 && (
                      <div style={styles.discountWarning}>
                        <div style={styles.discountWarningHeader}>
                          ⚠️ Discount Detected
                        </div>
                        <div style={styles.discountWarningContent}>
                          <p style={{ margin: 0 }}>
                            Found discount(s) on receipt but could not match to a free bag redemption.
                            {checkinResult?.loyalty_reward_available ?
                              ' Member has reward available - was it redeemed?' :
                              ' Member does not have a pending reward.'}
                          </p>
                          {checkinScanResult.discounts_detected?.map((d, idx) => (
                            <div key={idx} style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '4px' }}>
                              {d.description}: -${Math.abs(d.amount).toFixed(2)}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div style={styles.receiptActions}>
                      <button
                        onClick={() => {
                          setShowReceiptInCheckin(false);
                          resetCheckinReceipt();
                        }}
                        style={styles.cancelBtn}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCheckinReceiptSubmit}
                        disabled={checkinSaving || checkinScanResult.items.length === 0}
                        style={styles.confirmBtn}
                      >
                        {checkinSaving ? 'Saving...' : `Check In & Record Purchase`}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Members Tab */}
      {activeTab === 'members' && (
        <div style={styles.content}>
          <div style={styles.searchBar}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, phone, or member code..."
              style={styles.searchInput}
            />
          </div>

          <div style={styles.membersCount}>
            {filteredMembers.length} member{filteredMembers.length !== 1 ? 's' : ''} linked to your store
          </div>

          <div style={styles.table}>
            {/* Desktop Table */}
            <div className="portal-table-desktop" style={{ overflowX: 'auto' }}>
              <table style={styles.tableElement}>
                <thead>
                  <tr>
                    <th style={styles.th}>Member</th>
                    <th style={styles.th}>Contact</th>
                    <th style={styles.th}>Loyalty</th>
                    <th style={styles.th}>Animals</th>
                    <th style={styles.th}>Total Spent</th>
                    <th style={styles.th}>Last Visit</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMembers.map((member) => (
                    <tr
                      key={member.id}
                      onClick={() => navigate(`/portal/bridge-club/members/${member.id}`)}
                      style={styles.memberRow}
                    >
                      <td style={styles.td}>
                        <div style={styles.memberCellName}>
                          {member.first_name} {member.last_name}
                        </div>
                        <div style={styles.memberCellCode}>#{member.member_code}</div>
                      </td>
                      <td style={styles.td}>
                        <div>{member.email}</div>
                        <div style={styles.memberCellPhone}>{member.phone}</div>
                      </td>
                      <td style={styles.td}>
                        <div style={styles.loyaltyDotsSmall}>
                          {[...Array(10)].map((_, i) => (
                            <span
                              key={i}
                              style={{
                                ...styles.loyaltyDotSmall,
                                ...(i < member.loyalty_progress ? styles.loyaltyDotSmallFilled : {}),
                              }}
                            />
                          ))}
                        </div>
                        <div style={styles.loyaltyTextSmall}>
                          {member.loyalty_progress}/10
                          {member.loyalty_reward_available && ' - Reward!'}
                        </div>
                      </td>
                      <td style={styles.td}>{member.animals_count}</td>
                      <td style={styles.td}>${member.total_purchases.toFixed(2)}</td>
                      <td style={styles.td}>
                        {member.last_purchase_date
                          ? new Date(member.last_purchase_date).toLocaleDateString()
                          : 'Never'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="portal-cards-mobile">
              {filteredMembers.map((member) => (
                <div
                  key={member.id}
                  className="portal-mobile-card"
                  onClick={() => navigate(`/portal/bridge-club/members/${member.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="portal-mobile-card-header">
                    <span className="portal-mobile-card-title">{member.first_name} {member.last_name}</span>
                    <span style={{ fontSize: '0.85rem', color: '#508590' }}>#{member.member_code}</span>
                  </div>
                  <div className="portal-mobile-card-row">
                    <span className="portal-mobile-card-label">Contact</span>
                    <span className="portal-mobile-card-value" style={{ fontSize: '0.85rem' }}>{member.email || member.phone}</span>
                  </div>
                  <div className="portal-mobile-card-row">
                    <span className="portal-mobile-card-label">Loyalty</span>
                    <span className="portal-mobile-card-value">
                      {member.loyalty_progress}/10
                      {member.loyalty_reward_available && <span style={{ color: '#E73C3E', fontWeight: 'bold' }}> - Reward!</span>}
                    </span>
                  </div>
                  <div className="portal-mobile-card-row">
                    <span className="portal-mobile-card-label">Animals</span>
                    <span className="portal-mobile-card-value">{member.animals_count}</span>
                  </div>
                  <div className="portal-mobile-card-row">
                    <span className="portal-mobile-card-label">Total Spent</span>
                    <span className="portal-mobile-card-value" style={{ fontWeight: 'bold', color: '#E73C3E' }}>${member.total_purchases.toFixed(2)}</span>
                  </div>
                  <div className="portal-mobile-card-row">
                    <span className="portal-mobile-card-label">Last Visit</span>
                    <span className="portal-mobile-card-value">
                      {member.last_purchase_date ? new Date(member.last_purchase_date).toLocaleDateString() : 'Never'}
                    </span>
                  </div>
                  <div style={{ textAlign: 'right', marginTop: '0.5rem', color: '#508590', fontSize: '0.85rem' }}>
                    View Details →
                  </div>
                </div>
              ))}
            </div>

            {filteredMembers.length === 0 && (
              <div style={styles.noMembers}>
                {searchQuery ? 'No members match your search.' : 'No members linked to your store yet.'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Promotions Tab */}
      {activeTab === 'promotions' && (
        <PortalPromotions />
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <PortalBridgeClubAnalytics />
      )}

      <PortalFooter />
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '2rem 1.5rem',
    backgroundColor: '#FAF9F6',
    minHeight: '100vh',
  },
  header: {
    marginBottom: '1.5rem',
  },
  title: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#3F2021',
    margin: '0 0 0.5rem 0',
  },
  subtitle: {
    fontSize: '1.125rem',
    color: '#508590',
    margin: 0,
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
    fontSize: '1.125rem',
    color: '#508590',
  },
  error: {
    backgroundColor: '#fee2e2',
    border: '1px solid #fca5a5',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '4px',
    textAlign: 'center',
  },
  tabs: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1.5rem',
    borderBottom: '2px solid #C3AD8B',
    paddingBottom: '0.5rem',
    flexWrap: 'wrap',
  },
  tab: {
    padding: '0.75rem 1.5rem',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#3F2021',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
    borderRadius: '4px 4px 0 0',
    transition: 'all 0.2s',
  },
  tabActive: {
    backgroundColor: '#E73C3E',
    color: 'white',
  },
  content: {
    marginBottom: '2rem',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1.5rem',
    marginBottom: '2rem',
  },
  statCard: {
    backgroundColor: '#FFFFFF',
    padding: '1.5rem',
    borderRadius: '8px',
    border: '2px solid #C3AD8B',
    textAlign: 'center',
  },
  statValue: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: '#E73C3E',
    marginBottom: '0.5rem',
  },
  statLabel: {
    fontSize: '0.95rem',
    color: '#3F2021',
    fontWeight: '500',
  },
  statSub: {
    fontSize: '0.85rem',
    color: '#508590',
    marginTop: '0.25rem',
  },
  creditStatCard: {
    backgroundColor: '#f0fdf4',
    padding: '1.5rem',
    borderRadius: '8px',
    border: '2px solid #22c55e',
    textAlign: 'center',
  },
  creditStatValue: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: '#16a34a',
    marginBottom: '0.5rem',
  },
  creditStatSub: {
    fontSize: '0.85rem',
    color: '#16a34a',
    marginTop: '0.25rem',
    fontWeight: '500',
  },
  section: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    border: '2px solid #C3AD8B',
    padding: '1.5rem',
    marginBottom: '1.5rem',
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#3F2021',
    margin: '0 0 1rem 0',
  },
  actionsRow: {
    display: 'flex',
    gap: '1rem',
    flexWrap: 'wrap',
  },
  actionButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#E73C3E',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
  },
  actionButtonSecondary: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#508590',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
  },
  actionButtonTertiary: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#C3AD8B',
    color: '#3F2021',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
  },
  checkinCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    border: '2px solid #C3AD8B',
    padding: '1.5rem',
  },
  // QR Scanner styles
  scannerSection: {
    marginBottom: '1.5rem',
    textAlign: 'center',
  },
  qrScanner: {
    maxWidth: '400px',
    margin: '0 auto 1rem',
  },
  scannerError: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    padding: '0.5rem',
    borderRadius: '4px',
    marginBottom: '0.5rem',
    fontSize: '0.9rem',
  },
  scanQrBtn: {
    padding: '1rem 2rem',
    backgroundColor: '#E73C3E',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1.125rem',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  scanIcon: {
    fontSize: '1.5rem',
  },
  scannerToggleBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#6b7280',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  divider: {
    textAlign: 'center',
    color: '#508590',
    margin: '1rem 0',
    fontSize: '0.95rem',
  },
  checkinHelp: {
    color: '#508590',
    marginBottom: '1rem',
  },
  searchRow: {
    display: 'flex',
    gap: '0.75rem',
    marginBottom: '1rem',
    flexWrap: 'wrap',
  },
  searchInput: {
    flex: 1,
    minWidth: '200px',
    padding: '0.75rem',
    border: '1px solid #C3AD8B',
    borderRadius: '4px',
    fontSize: '1rem',
  },
  searchButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#E73C3E',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
  },
  clearButton: {
    padding: '0.75rem 1rem',
    backgroundColor: '#6b7280',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
  },
  errorBox: {
    backgroundColor: '#fee2e2',
    border: '1px solid #fca5a5',
    color: '#991b1b',
    padding: '0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
  },
  successBox: {
    backgroundColor: '#d1fae5',
    border: '1px solid #6ee7b7',
    color: '#065f46',
    padding: '0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
  },
  memberCard: {
    backgroundColor: '#FAF9F6',
    borderRadius: '8px',
    border: '2px solid #C3AD8B',
    padding: '1.5rem',
  },
  memberHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1rem',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  memberName: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#3F2021',
  },
  memberCode: {
    fontSize: '0.95rem',
    color: '#508590',
  },
  rewardBadge: {
    backgroundColor: '#E73C3E',
    color: 'white',
    padding: '0.5rem 1rem',
    borderRadius: '4px',
    fontSize: '0.875rem',
    fontWeight: 'bold',
  },
  memberDetails: {
    marginBottom: '1rem',
  },
  detailRow: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '0.25rem',
    flexWrap: 'wrap',
  },
  detailLabel: {
    color: '#508590',
    fontWeight: '500',
    minWidth: '100px',
  },
  loyaltySection: {
    backgroundColor: '#FFFFFF',
    padding: '1rem',
    borderRadius: '4px',
    marginBottom: '1rem',
  },
  loyaltyLabel: {
    fontWeight: '500',
    color: '#3F2021',
    marginBottom: '0.5rem',
  },
  loyaltyDots: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '0.5rem',
    flexWrap: 'wrap',
  },
  loyaltyDot: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    border: '2px solid #C3AD8B',
    backgroundColor: '#FAF9F6',
  },
  loyaltyDotFilled: {
    backgroundColor: '#E73C3E',
    borderColor: '#E73C3E',
  },
  loyaltySub: {
    fontSize: '0.875rem',
    color: '#508590',
  },
  animalsSection: {
    marginBottom: '1rem',
  },
  animalsLabel: {
    fontWeight: '500',
    color: '#3F2021',
    marginBottom: '0.5rem',
  },
  animalsList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  animalChip: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #C3AD8B',
    padding: '0.25rem 0.75rem',
    borderRadius: '12px',
    fontSize: '0.875rem',
  },
  rbTag: {
    backgroundColor: '#E73C3E',
    color: 'white',
    padding: '0.1rem 0.4rem',
    borderRadius: '4px',
    marginLeft: '0.25rem',
    fontSize: '0.7rem',
    fontWeight: 'bold',
  },
  // Feed Bins Section - compact cards for check-in
  feedBinsSection: {
    marginBottom: '1rem',
    padding: '0.75rem',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
  },
  feedBinsHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '0.75rem',
  },
  feedBinsLabel: {
    fontWeight: '600',
    color: '#3F2021',
    fontSize: '0.95rem',
  },
  feedBinsAlert: {
    backgroundColor: '#dc2626',
    color: 'white',
    padding: '0.25rem 0.75rem',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: '700',
    animation: 'pulse 2s infinite',
  },
  feedBinsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
    gap: '0.5rem',
  },
  feedBinCard: {
    backgroundColor: '#ffffff',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    padding: '0.5rem',
    textAlign: 'center' as const,
  },
  feedBinTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.25rem',
    marginBottom: '0.25rem',
  },
  feedBinIcon: {
    fontSize: '1rem',
  },
  feedBinName: {
    fontSize: '0.75rem',
    fontWeight: '600',
    color: '#374151',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    maxWidth: '70px',
  },
  feedBinMiddle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.25rem',
    marginBottom: '0.25rem',
  },
  feedBinPercent: {
    fontSize: '1rem',
    fontWeight: '700',
  },
  feedBinBadge: {
    color: 'white',
    padding: '0.1rem 0.4rem',
    borderRadius: '4px',
    fontSize: '0.6rem',
    fontWeight: '700',
    textTransform: 'uppercase' as const,
  },
  feedBinBottom: {
    fontSize: '0.7rem',
    color: '#6b7280',
  },
  feedBinSuggestion: {
    marginTop: '0.25rem',
    padding: '0.25rem',
    backgroundColor: '#fef2f2',
    borderRadius: '4px',
    fontSize: '0.65rem',
    color: '#991b1b',
    fontWeight: '500',
  },
  // Trip Planner Calculator styles
  tripPlannerToggle: {
    marginTop: '0.75rem',
    borderTop: '1px solid #e5e7eb',
    paddingTop: '0.5rem',
  },
  tripPlannerToggleBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#508590',
    fontWeight: '600',
    fontSize: '0.85rem',
    padding: '0.25rem 0',
  },
  tripPlannerSection: {
    marginTop: '0.5rem',
    padding: '0.75rem',
    backgroundColor: '#f0fdf4',
    borderRadius: '8px',
    border: '1px solid #bbf7d0',
  },
  tripPlannerQuestion: {
    fontWeight: '600',
    color: '#166534',
    marginBottom: '0.5rem',
    fontSize: '0.85rem',
  },
  tripPlannerOptions: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap' as const,
    marginBottom: '0.75rem',
  },
  tripPlannerOption: {
    padding: '0.4rem 0.75rem',
    border: '1px solid #86efac',
    borderRadius: '16px',
    backgroundColor: '#ffffff',
    color: '#166534',
    fontSize: '0.75rem',
    cursor: 'pointer',
    fontWeight: '500',
  },
  tripPlannerOptionActive: {
    backgroundColor: '#166534',
    color: '#ffffff',
    borderColor: '#166534',
  },
  tripPlannerResults: {
    borderTop: '1px solid #bbf7d0',
    paddingTop: '0.75rem',
  },
  tripPlannerResultsHeader: {
    fontWeight: '600',
    color: '#166534',
    marginBottom: '0.5rem',
    fontSize: '0.8rem',
  },
  tripPlannerItem: {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '0.5rem',
    marginBottom: '0.5rem',
  },
  tripPlannerItemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontWeight: '600',
    fontSize: '0.8rem',
    marginBottom: '0.25rem',
  },
  tripPlannerBagsNeeded: {
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    padding: '0.15rem 0.5rem',
    borderRadius: '8px',
    fontSize: '0.7rem',
    fontWeight: '700',
  },
  tripPlannerBagsOk: {
    backgroundColor: '#dcfce7',
    color: '#166534',
    padding: '0.15rem 0.5rem',
    borderRadius: '8px',
    fontSize: '0.7rem',
    fontWeight: '600',
  },
  tripPlannerItemDetails: {
    fontSize: '0.75rem',
    color: '#374151',
  },
  tripPlannerSummary: {
    marginTop: '0.75rem',
    padding: '0.75rem',
    backgroundColor: '#166534',
    color: '#ffffff',
    borderRadius: '6px',
    fontSize: '0.8rem',
  },
  purchaseSection: {
    backgroundColor: '#FFFFFF',
    padding: '1rem',
    borderRadius: '4px',
    marginBottom: '1rem',
  },
  purchaseTitle: {
    fontWeight: '500',
    color: '#3F2021',
    marginBottom: '0.75rem',
  },
  purchaseRow: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
  },
  purchaseField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  purchaseLabel: {
    fontSize: '0.875rem',
    color: '#508590',
  },
  purchaseInput: {
    padding: '0.5rem',
    border: '1px solid #C3AD8B',
    borderRadius: '4px',
    width: '120px',
  },
  recordButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#508590',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: '500',
  },
  redeemSection: {
    textAlign: 'center',
  },
  redeemButton: {
    padding: '1rem 2rem',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1.125rem',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  // Available rewards banner
  rewardsBanner: {
    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    borderRadius: '12px',
    padding: '1rem',
    marginBottom: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    boxShadow: '0 4px 12px rgba(245, 158, 11, 0.4)',
    flexWrap: 'wrap',
  },
  rewardsBannerIcon: {
    fontSize: '2rem',
  },
  rewardsBannerText: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '0.5rem',
    flex: 1,
  },
  rewardsBannerCount: {
    fontSize: '2rem',
    fontWeight: '800',
    color: 'white',
  },
  rewardsBannerLabel: {
    fontSize: '1rem',
    fontWeight: '700',
    color: 'white',
  },
  rewardsBannerHint: {
    fontSize: '0.85rem',
    color: 'rgba(255, 255, 255, 0.9)',
    width: '100%',
    textAlign: 'center',
    marginTop: '0.25rem',
  },
  // Bin alert styles
  binAlertBanner: {
    backgroundColor: '#fef2f2',
    border: '2px solid #fca5a5',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1rem',
  },
  binAlertHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '0.75rem',
  },
  binAlertIcon: {
    fontSize: '1.25rem',
  },
  binAlertTitle: {
    fontWeight: '700',
    color: '#991b1b',
    fontSize: '1rem',
  },
  binAlertList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
  },
  binAlertItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap' as const,
    backgroundColor: 'white',
    padding: '0.5rem 0.75rem',
    borderRadius: '6px',
  },
  binAlertStatus: {
    color: 'white',
    fontSize: '0.7rem',
    fontWeight: '700',
    padding: '0.15rem 0.4rem',
    borderRadius: '4px',
    textTransform: 'uppercase' as const,
  },
  binAlertName: {
    fontWeight: '600',
    color: '#3F2021',
  },
  binAlertLevel: {
    color: '#666',
    fontSize: '0.85rem',
  },
  binAlertDays: {
    color: '#dc2626',
    fontSize: '0.85rem',
    fontWeight: '500',
  },
  binAlertViewBtn: {
    marginTop: '0.75rem',
    width: '100%',
    padding: '0.5rem',
    backgroundColor: '#508590',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  // Primary check-in actions
  primaryActions: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
  },
  scanReceiptMainBtn: {
    flex: 1,
    minWidth: '200px',
    padding: '1rem 1.5rem',
    backgroundColor: '#E73C3E',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1.125rem',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
  },
  checkinOnlyBtn: {
    flex: 1,
    minWidth: '200px',
    padding: '1rem 1.5rem',
    backgroundColor: '#FAF9F6',
    color: '#3F2021',
    border: '2px solid #C3AD8B',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
  },
  // Inline receipt scanner styles
  receiptScannerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  receiptScannerTitle: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#3F2021',
    margin: 0,
  },
  closeReceiptBtn: {
    width: '32px',
    height: '32px',
    padding: 0,
    backgroundColor: '#6b7280',
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    fontSize: '1.25rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  memberBanner: {
    backgroundColor: '#d1fae5',
    border: '2px solid #6ee7b7',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1.5rem',
  },
  // Receipt scanning styles
  stepSection: {
    marginTop: '1.5rem',
  },
  stepHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '0.75rem',
  },
  stepNumber: {
    backgroundColor: '#E73C3E',
    color: 'white',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '0.9rem',
  },
  stepTitle: {
    fontSize: '1.1rem',
    fontWeight: '500',
    color: '#3F2021',
  },
  stepHelp: {
    color: '#508590',
    marginBottom: '1rem',
    fontSize: '0.95rem',
  },
  selectedMemberBanner: {
    backgroundColor: '#d1fae5',
    border: '2px solid #6ee7b7',
    borderRadius: '8px',
    padding: '1rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  memberCodeSmall: {
    color: '#508590',
    fontSize: '0.9rem',
  },
  memberLoyaltySmall: {
    fontSize: '0.85rem',
    color: '#065f46',
    marginTop: '0.25rem',
  },
  changeMemberBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#6b7280',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  uploadArea: {
    border: '2px dashed #C3AD8B',
    borderRadius: '8px',
    padding: '3rem 2rem',
    textAlign: 'center',
    cursor: 'pointer',
    backgroundColor: '#FAF9F6',
    transition: 'border-color 0.2s',
  },
  uploadIcon: {
    fontSize: '3rem',
    display: 'block',
    marginBottom: '0.5rem',
  },
  uploadText: {
    fontSize: '1.1rem',
    fontWeight: '500',
    color: '#3F2021',
    margin: '0 0 0.25rem 0',
  },
  uploadHint: {
    fontSize: '0.9rem',
    color: '#508590',
    margin: 0,
  },
  // Camera-based receipt scanning styles
  cameraContainer: {
    position: 'relative',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: '#000',
    minHeight: '300px',
  },
  cameraVideo: {
    width: '100%',
    height: 'auto',
    minHeight: '300px',
    maxHeight: '500px',
    objectFit: 'cover',
    display: 'block',
    cursor: 'crosshair',
  },
  tapToFocusHint: {
    position: 'absolute' as const,
    top: '10px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    color: '#fff',
    padding: '4px 12px',
    borderRadius: '4px',
    fontSize: '0.75rem',
    pointerEvents: 'none' as const,
  },
  cameraControls: {
    display: 'flex',
    justifyContent: 'center',
    gap: '1rem',
    padding: '1rem',
    backgroundColor: 'rgba(0,0,0,0.7)',
  },
  cameraCancelBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#6b7280',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    cursor: 'pointer',
  },
  cameraCaptureBtn: {
    padding: '0.75rem 2rem',
    backgroundColor: '#E73C3E',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1.1rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  receiptCaptureOptions: {
    textAlign: 'center',
    padding: '2rem',
    border: '2px dashed #C3AD8B',
    borderRadius: '8px',
    backgroundColor: '#FAF9F6',
  },
  scanReceiptPrimaryBtn: {
    padding: '1rem 2rem',
    backgroundColor: '#E73C3E',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1.1rem',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  cameraErrorText: {
    color: '#dc2626',
    fontSize: '0.9rem',
    marginTop: '0.5rem',
    marginBottom: '0',
  },
  orDivider: {
    color: '#6b7280',
    margin: '1rem 0',
    fontSize: '0.9rem',
  },
  uploadFromDeviceBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: 'transparent',
    color: '#508590',
    border: '1px solid #508590',
    borderRadius: '6px',
    fontSize: '0.95rem',
    cursor: 'pointer',
  },
  scanningOverlay: {
    textAlign: 'center',
    padding: '3rem',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #C3AD8B',
    borderTop: '4px solid #E73C3E',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 1rem',
  },
  receiptPreview: {
    marginBottom: '1rem',
    textAlign: 'center',
  },
  receiptImage: {
    maxWidth: '200px',
    maxHeight: '150px',
    borderRadius: '4px',
    border: '1px solid #C3AD8B',
  },
  retakeBtn: {
    display: 'block',
    margin: '0.5rem auto 0',
    padding: '0.25rem 0.75rem',
    backgroundColor: '#6b7280',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  itemsSection: {
    marginBottom: '1rem',
  },
  itemsSectionTitle: {
    fontSize: '1rem',
    fontWeight: '500',
    color: '#3F2021',
    marginBottom: '0.75rem',
  },
  itemRow: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '0.5rem',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  itemNameInput: {
    flex: 1,
    minWidth: '150px',
    padding: '0.5rem',
    border: '1px solid #C3AD8B',
    borderRadius: '4px',
    fontSize: '0.9rem',
  },
  itemQtyInput: {
    width: '60px',
    padding: '0.5rem',
    border: '1px solid #C3AD8B',
    borderRadius: '4px',
    fontSize: '0.9rem',
    textAlign: 'center',
  },
  itemPriceInput: {
    width: '80px',
    padding: '0.5rem',
    border: '1px solid #C3AD8B',
    borderRadius: '4px',
    fontSize: '0.9rem',
  },
  rbCheckLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    cursor: 'pointer',
  },
  rbCheckText: {
    fontSize: '0.85rem',
    fontWeight: '500',
    color: '#E73C3E',
  },
  removeItemBtn: {
    width: '28px',
    height: '28px',
    padding: 0,
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1.2rem',
    lineHeight: 1,
  },
  addItemBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#FAF9F6',
    border: '1px dashed #C3AD8B',
    borderRadius: '4px',
    color: '#508590',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  receiptSummary: {
    backgroundColor: '#FAF9F6',
    borderRadius: '4px',
    padding: '1rem',
    marginBottom: '1rem',
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '0.5rem',
    fontSize: '0.95rem',
  },
  summaryRowHighlight: {
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: '#d1fae5',
    padding: '0.5rem',
    borderRadius: '4px',
    marginTop: '0.5rem',
    fontWeight: '500',
    color: '#065f46',
  },
  receiptActions: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
  },
  cancelBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#6b7280',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
  },
  confirmBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '500',
  },
  newScanBtn: {
    display: 'block',
    marginTop: '0.75rem',
    padding: '0.5rem 1rem',
    backgroundColor: '#508590',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  confirmBtnLarge: {
    padding: '1rem 2rem',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1.1rem',
    fontWeight: '600',
    width: '100%',
  },
  // Smart Summary styles
  smartSummaryCard: {
    backgroundColor: '#f0fdf4',
    border: '2px solid #10b981',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1rem',
  },
  smartSummaryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '1.25rem',
    paddingBottom: '1rem',
    borderBottom: '1px solid #d1fae5',
  },
  smartSummaryIcon: {
    fontSize: '1.5rem',
    color: '#10b981',
    fontWeight: 'bold',
  },
  smartSummaryTitle: {
    fontSize: '1.25rem',
    fontWeight: '700',
    color: '#065f46',
    letterSpacing: '0.05em',
  },
  smartSummarySection: {
    marginBottom: '1rem',
  },
  smartSummarySectionHighlight: {
    backgroundColor: '#dcfce7',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1rem',
    border: '1px solid #86efac',
  },
  smartSummaryLabel: {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '0.5rem',
  },
  smartSummaryValue: {
    fontSize: '1.25rem',
    fontWeight: '700',
    color: '#1f2937',
  },
  smartSummarySubtext: {
    fontSize: '0.875rem',
    color: '#4b5563',
    marginTop: '0.25rem',
  },
  newRewardBadge: {
    backgroundColor: '#fbbf24',
    color: '#78350f',
    padding: '0.125rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '700',
    marginLeft: '0.5rem',
  },
  redemptionItem: {
    marginBottom: '0.75rem',
  },
  redemptionProduct: {
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: '0.25rem',
  },
  redemptionDetails: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
    fontSize: '0.875rem',
  },
  redemptionMsrp: {
    color: '#059669',
  },
  redemptionCredit: {
    color: '#508590',
    fontWeight: '600',
  },
  redemptionAlert: {
    backgroundColor: '#d1fae5',
    border: '1px solid #6ee7b7',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1rem',
  },
  redemptionAlertHeader: {
    fontWeight: '700',
    fontSize: '1rem',
    color: '#065f46',
    marginBottom: '0.5rem',
  },
  redemptionAlertContent: {
    color: '#047857',
    fontSize: '0.875rem',
  },
  discountWarning: {
    backgroundColor: '#fef3c7',
    border: '1px solid #fcd34d',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1rem',
  },
  discountWarningHeader: {
    fontWeight: '700',
    fontSize: '1rem',
    color: '#92400e',
    marginBottom: '0.5rem',
  },
  discountWarningContent: {
    color: '#b45309',
    fontSize: '0.875rem',
  },
  totalCreditBox: {
    marginTop: '0.75rem',
    paddingTop: '0.75rem',
    borderTop: '1px solid #86efac',
    color: '#065f46',
    fontSize: '1rem',
  },
  smartSummaryNote: {
    fontSize: '0.875rem',
    color: '#6b7280',
    fontStyle: 'italic',
    padding: '0.75rem',
    backgroundColor: '#f9fafb',
    borderRadius: '6px',
    marginBottom: '1rem',
  },
  smartSummaryMemberStatus: {
    marginTop: '1rem',
    paddingTop: '1rem',
    borderTop: '1px solid #d1fae5',
  },
  memberStatusRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.375rem 0',
    fontSize: '0.9375rem',
    color: '#374151',
  },
  // Members table styles
  searchBar: {
    marginBottom: '1rem',
  },
  membersCount: {
    marginBottom: '1rem',
    color: '#508590',
  },
  table: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    border: '2px solid #C3AD8B',
    overflow: 'auto',
  },
  tableElement: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '600px',
  },
  th: {
    textAlign: 'left',
    padding: '0.75rem',
    borderBottom: '2px solid #C3AD8B',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#3F2021',
    backgroundColor: '#FAF9F6',
  },
  td: {
    padding: '0.75rem',
    borderBottom: '1px solid #C3AD8B',
    fontSize: '0.9rem',
    color: '#3F2021',
  },
  memberRow: {
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  },
  memberCellName: {
    fontWeight: '500',
  },
  memberCellCode: {
    fontSize: '0.8rem',
    color: '#508590',
  },
  memberCellPhone: {
    fontSize: '0.8rem',
    color: '#508590',
  },
  loyaltyDotsSmall: {
    display: 'flex',
    gap: '3px',
  },
  loyaltyDotSmall: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    border: '1px solid #C3AD8B',
    backgroundColor: '#FAF9F6',
  },
  loyaltyDotSmallFilled: {
    backgroundColor: '#E73C3E',
    borderColor: '#E73C3E',
  },
  loyaltyTextSmall: {
    fontSize: '0.75rem',
    color: '#508590',
    marginTop: '2px',
  },
  noMembers: {
    textAlign: 'center',
    padding: '2rem',
    color: '#508590',
  },
  // Clickable stat cards
  statCardClickable: {
    backgroundColor: '#FFFFFF',
    padding: '1.5rem',
    borderRadius: '8px',
    border: '2px solid #C3AD8B',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  creditStatCardClickable: {
    backgroundColor: '#f0fdf4',
    padding: '1.5rem',
    borderRadius: '8px',
    border: '2px solid #22c55e',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  clickHint: {
    fontSize: '0.75rem',
    color: '#508590',
    marginTop: '0.5rem',
  },
  clickHintGreen: {
    fontSize: '0.75rem',
    color: '#16a34a',
    marginTop: '0.5rem',
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
  },
  modal: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    maxWidth: '500px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  modalWide: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    maxWidth: '600px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 1.5rem',
    borderBottom: '1px solid #C3AD8B',
  },
  modalTitle: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#3F2021',
    margin: 0,
  },
  modalClose: {
    width: '32px',
    height: '32px',
    backgroundColor: '#6b7280',
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    fontSize: '1.5rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  },
  modalBody: {
    padding: '1.5rem',
  },
  modalSubtitle: {
    color: '#508590',
    marginBottom: '1rem',
    fontSize: '1rem',
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '1rem',
    padding: '1rem 1.5rem',
    borderTop: '1px solid #C3AD8B',
  },
  // Product selection
  productList: {
    maxHeight: '300px',
    overflowY: 'auto',
    marginBottom: '1rem',
  },
  productOption: {
    padding: '0.75rem 1rem',
    border: '1px solid #C3AD8B',
    borderRadius: '4px',
    marginBottom: '0.5rem',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  productOptionSelected: {
    backgroundColor: '#d1fae5',
    borderColor: '#10b981',
  },
  productName: {
    fontWeight: '500',
    color: '#3F2021',
    marginBottom: '0.25rem',
  },
  formBadge: {
    fontSize: '0.8rem',
    color: '#508590',
    fontWeight: '400',
  },
  productDetails: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.9rem',
    color: '#508590',
  },
  productPrice: {
    fontWeight: '600',
    color: '#10b981',
  },
  noProducts: {
    textAlign: 'center',
    color: '#508590',
    padding: '2rem',
  },
  animalTypeHeader: {
    fontSize: '0.85rem',
    fontWeight: '600',
    color: '#508590',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    padding: '0.75rem 0 0.25rem 0',
    borderBottom: '1px solid #C3AD8B',
    marginBottom: '0.5rem',
    marginTop: '0.5rem',
  },
  selectedSummary: {
    backgroundColor: '#f0fdf4',
    padding: '1rem',
    borderRadius: '4px',
    border: '1px solid #6ee7b7',
    fontSize: '0.95rem',
  },
  // Redemption history
  historyStats: {
    display: 'flex',
    gap: '2rem',
    marginBottom: '1rem',
    fontSize: '0.95rem',
    color: '#508590',
  },
  noHistory: {
    textAlign: 'center',
    color: '#508590',
    padding: '2rem',
  },
  historyList: {
    maxHeight: '400px',
    overflowY: 'auto',
  },
  historyItem: {
    padding: '0.75rem',
    borderBottom: '1px solid #C3AD8B',
  },
  historyMember: {
    marginBottom: '0.25rem',
  },
  historyCode: {
    fontSize: '0.85rem',
    color: '#508590',
    marginLeft: '0.5rem',
  },
  historyProduct: {
    fontSize: '0.9rem',
    color: '#3F2021',
    marginBottom: '0.25rem',
  },
  historyMeta: {
    display: 'flex',
    gap: '1rem',
    fontSize: '0.85rem',
  },
  historyCost: {
    fontWeight: '600',
    color: '#10b981',
  },
  historyDate: {
    color: '#508590',
  },
  // Credits detail modal
  creditsSummary: {
    textAlign: 'center',
    marginBottom: '1.5rem',
  },
  creditsBig: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: '#16a34a',
  },
  creditsLabel: {
    fontSize: '1rem',
    color: '#508590',
    marginBottom: '0.5rem',
  },
  creditsNote: {
    fontSize: '0.9rem',
    color: '#6b7280',
  },
  creditsList: {
    borderTop: '1px solid #C3AD8B',
    paddingTop: '1rem',
  },
  creditsListTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#3F2021',
    marginBottom: '1rem',
  },
  creditItem: {
    padding: '0.75rem',
    backgroundColor: '#FAF9F6',
    borderRadius: '4px',
    marginBottom: '0.5rem',
  },
  creditDescription: {
    fontSize: '0.9rem',
    color: '#3F2021',
    marginBottom: '0.25rem',
  },
  creditMeta: {
    display: 'flex',
    gap: '1rem',
    fontSize: '0.85rem',
  },
  creditAmount: {
    fontWeight: '600',
    color: '#10b981',
  },
  creditStatus: {
    textTransform: 'capitalize',
  },
  creditDate: {
    color: '#508590',
  },
};

export default PortalBridgeClub;
